/**
 * The plan Edit-modal BODY (P1 Task 2 of the PlanEditor extraction, see
 * `.superpowers/sdd/2026-08-21_P1_PLAN_EDITOR_EXTRACTION/`).
 *
 * Moved verbatim out of `CatalogAdmin.tsx`'s `PlansSection` — the scalar
 * fields + `PlanUploads` + `PlanCycle` + `PlanRateLimitsEditor` +
 * `PlanPrices` + `LlmPricing` + `FeaturePricingEditor` + `PlanTenantControls`
 * + the aggregate `SaveBar` — so it can be reused for a plan-CREATE draft
 * (P2) and the per-tenant Plan tab (P4) behind the same `PlanEditorScope`
 * union. P1 wired up only `scope.kind === 'plan'`; P2 Task 1 adds
 * `plan-create` (an in-memory draft — see `PlanCreateDraft`/`useDraftSavers`
 * below). `tenant` (P4) is still not reachable and renders nothing.
 *
 * The `<Modal>` SHELL stays in `CatalogAdmin.tsx`'s `PlansSection` — this
 * component is only what renders INSIDE it. That keeps the open/close/URL
 * wiring (`openPlanId` / `onOpenPlan` / `onClosePlan`) exactly where it was.
 *
 * P1 Task 3: `savers` (`PlanSavers`) is now wired into every sub-editor whose
 * tenant-scope equivalent (P4) is `budgetApi` rather than `catalogAdminApi` —
 * `PlanUploads` / `PlanCycle` / `PlanRateLimitsEditor` / the scalar fields
 * below. `PlanPrices` / `LlmPricing` / `FeaturePricingEditor` stay on their
 * own `scope`/`scopeId` calls into `catalogAdminApi` directly — per-currency
 * prices, model access and metered pricing are billing-package-internal on
 * BOTH scopes (a tenant's Plan tab edits the same rows via the same
 * scope='tenant' routes those components already take), so there is no
 * cycle to avoid there and no reason to add an indirection nothing needs.
 *
 * The scalar section's `useRegisterSection` used to live in `PlansSection`
 * (`savePlans`, driving a multi-row batch that was already dead in practice —
 * the plan list is read-only, only the OPEN plan's draft can ever be dirty).
 * It moved here because this is the one place the injected `savers` and this
 * plan's own `draft` are both already in scope; `onScalarSaved` reports the
 * stale-entitlements result back up to `CatalogAdmin`'s page-level banner,
 * which cannot be owned by a per-modal component. See
 * `CatalogAdmin.tsx`'s `handleScalarSaved` for that half.
 */
import { useEffect, useMemo, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { CapInput, GbInput, InheritedCapField, ModelAccessEditor, clearDraft, isDraftInFlight, useConfirm, useDraftInFlight, useDraftState, useRegisterSection, useToast, type PlanEditBar } from '@papan/core-ui/ui'
import {
  DEFAULT_AGENT_CONCURRENCY_CAP, DEFAULT_PLAN_FAIRNESS_WEIGHT,
  PLAN_FAIRNESS_WEIGHT_MIN, PLAN_FAIRNESS_WEIGHT_MAX,
  formatMoney, resolvePlanFieldDefaults, type PlanFieldDefaults,
} from '@papan/types'
import { catalogAdminApi } from '../client/catalog-admin.js'
import { pinnedBatch, type PinnedBatch } from '@papan/api-client'
import type { AdminPlanView } from '../client/catalog-admin.js'
import { parseOptionalInt, parsePrice, inputToMicro, microToInput, priceToInput, isValidRateField, modelPriceFieldStatus } from './catalog-units.js'
import {
  InfoHint, PlanUploads, PlanCycle, PlanRateLimitsEditor,
  LlmPricing, FeaturePricingEditor, PlanTenantControls, SaveBar, planDraft,
  type PlanDraft, type T, type MeteredDraft, type LlmPricingDraft,
} from './CatalogAdmin.js'
import type { PlanEditorScope, PlanSavers, PlanScalarPatch, PlanScalarSaveResult } from './plan-editor-scope.js'

/** An empty staged delta — one stable value for the drafts' fallback (#897). */
const NO_DRAFT = Object.freeze({})

/** Every draft the `plan-create` scope keeps (#897) — the new plan itself and
 *  the sections / typed boxes inside it. */
const PLAN_CREATE_DRAFT_KEYS = [
  'planCreate:new',
  'planCreate:new:uploads',
  'planCreate:new:rateLimits',
  'planCreate:new:typed:storage',
  'planCreate:new:typed:concurrency',
] as const

/**
 * Drop the new-plan draft NOW — for the host closing the create dialog
 * (dismissed, or the plan created). The store would drop it a tick after the
 * dialog unmounts anyway; doing it here means a reopen in the same tick also
 * starts blank.
 */
export function clearPlanCreateDrafts(): void {
  for (const k of PLAN_CREATE_DRAFT_KEYS) clearDraft(k)
}

/**
 * The plan's whole price-set under ONE currency selector — Rows 5–9 of the
 * plan-modal redesign (Phase 5, `docs/plans/2026-08-24_PLAN_MODAL_REDESIGN.md`).
 * An operator picks a currency once and sets everything priced in it:
 *
 *   Row 5 — the selector (grouped priced / supported-not-priced) + a
 *           remove-currency control (billing currency locked while active).
 *   Row 6 — the SELECTED currency's `billing_plan_prices` row, as inline
 *           inputs: Free credits (`base_pool`), Extra (`extra_pool`), Grace
 *           (`grace_pool`), and — provider only — Price per extra seat
 *           (`base_price_minor`). One FULL-ROW `setPlanPrice` per save (the
 *           whole money row is re-sent or an omitted column is zeroed).
 *   Rows 7–8 — metered feature pricing (`FeaturePricingEditor`, server-ordered
 *           sandbox → web), rendered BEFORE the models table.
 *   Row 9 — the AI models table (`LlmPricing`).
 *
 * **`PlanPrices` (the old multi-row per-currency table) was removed** in this
 * phase — its per-currency editing folded into Row 6 (one currency at a time,
 * chosen by the selector) and its remove-currency affordance into Row 5.
 *
 * **Group 1 ("priced") vs Group 2 ("supported, not yet priced")** splits
 * `plan.prices` (a real `billing_plan_prices` row exists) from `currencies`
 * (deployment-supported) minus that set.
 *
 * **Only two currency sets ever affect a real charge** (design §4): the
 * deployment's own billing currency (labelled load-bearing/"Billing") and USD
 * (the fallback pricing anchor other per-model overrides resolve through, §3.5
 * — never "not charged", so it does NOT get the reserved note). Any OTHER
 * currency's set is stored but not charged on a single-deployment-currency
 * system, and the panel says so rather than implying it is live.
 */
export function PriceSetPanel({
  plan, currencies, defaultCurrency, planAuthority, busy, onSaved, bar, barTotal, t,
}: {
  plan: AdminPlanView
  /** Every ISO currency the deployment supports — the selector's option list. */
  currencies: string[]
  /** The deployment's own billing currency — the load-bearing price-set and
   *  the selector's default selection. */
  defaultCurrency: string
  planAuthority: 'provider' | 'manual'
  busy: boolean
  onSaved: () => Promise<void>
  bar?: PlanEditBar
  /** The host's aggregate staged-edit count. Switching the currency re-seeds
   *  the Row-6 money inputs and refetches/RESETS the model + metered price
   *  drafts, so a non-zero count means a switch could discard unsaved price
   *  edits — we confirm first. */
  barTotal?: number
  t: T
}) {
  const bc = defaultCurrency.toLowerCase()
  // #897 — the currency being edited and its Row-6 money drafts are kept
  // across a phone/desktop layout swap (`plan:<id>:…`). The currency rides
  // along so the swap lands on the currency whose draft it carried.
  const draftBase = `plan:${plan.id}`
  const [selectedCurrency, setSelectedCurrency] = useDraftState(`${draftBase}:priceCurrency`, bc)
  const confirm = useConfirm()
  const toast = useToast()
  const provider = planAuthority === 'provider'

  // Switching currency reloads `LlmPricing`/`FeaturePricingEditor` AND re-seeds
  // the Row-6 money inputs below, discarding any unsaved edit in the current
  // currency. If anything is staged (aggregate count — over-broad on purpose,
  // so the confirm can only ever be a harmless extra prompt, never a silent
  // loss), confirm before switching.
  const switchCurrency = async (next: string) => {
    if (next === selectedCurrency) return
    if (barTotal && barTotal > 0) {
      const ok = await confirm({
        title: t('catalog.plans.priceset.switchTitle', { defaultValue: 'Discard unsaved price edits?' }),
        message: t('catalog.plans.priceset.switchBody', {
          defaultValue: 'Switching the price currency reloads this currency’s free-credits, extra, grace and per-seat inputs, plus the AI-model and metered prices. Any unsaved edits in the current currency will be discarded.',
        }),
        confirmText: t('catalog.plans.priceset.switchConfirm', { defaultValue: 'Switch currency' }),
        type: 'warning',
      })
      if (!ok) return
    }
    setSelectedCurrency(next)
  }

  // Group 1: currencies this plan already HAS a `billing_plan_prices` row
  // for — billing currency first, then alphabetical.
  const pricedCurrencies = useMemo(() => {
    const set = new Set(plan.prices.map(pr => pr.currency.toLowerCase()))
    return Array.from(set).sort((a, b) => (a === bc ? -1 : b === bc ? 1 : a.localeCompare(b)))
  }, [plan.prices, bc])
  const pricedSet = useMemo(() => new Set(pricedCurrencies), [pricedCurrencies])
  // Group 2: deployment-supported currencies with no row yet.
  const unpricedCurrencies = useMemo(
    () => currencies.map(c => c.toLowerCase()).filter(c => !pricedSet.has(c)).sort(),
    [currencies, pricedSet],
  )

  const isBillingCurrency = selectedCurrency === bc
  // USD is the fallback pricing anchor (design §3.5) — always consulted, so it
  // is not "reserved/not-charged" even when it differs from the billing
  // currency. Only a THIRD currency (neither billing nor USD) gets that note.
  const isReserved = !isBillingCurrency && selectedCurrency !== 'usd'

  // ── Row 6: the SELECTED currency's money row (`billing_plan_prices`) ──────
  //
  // The plan's existing row for this currency (`null` = unpriced — the Row-6
  // save creates it). Free credits / extra pool / grace pool are always shown;
  // the per-extra-seat price only under provider billing (under manual the
  // real seat charge is a per-tenant contract, so this field is inert/hidden).
  const selectedRow = useMemo(
    () => plan.prices.find(pr => pr.currency === selectedCurrency) ?? null,
    [plan.prices, selectedCurrency],
  )
  type MoneyDraft = { free: string; extra: string; grace: string; price: string }
  const seedMoney = (): MoneyDraft => ({
    free: selectedRow ? microToInput(selectedRow.basePoolBillingMicro) : '',
    extra: selectedRow ? microToInput(selectedRow.extraPoolBillingMicro) : '',
    grace: selectedRow ? microToInput(selectedRow.gracePoolBillingMicro) : '',
    price: selectedRow ? priceToInput(selectedRow.basePriceMinor, selectedCurrency) : '',
  })
  // A draft over the selected currency's stored row, keyed per currency: until
  // edited it IS the row (a reload after Save shows through); once edited no
  // reload stomps it. Switching currency switches the key — the old currency's
  // draft is dropped, as the re-seed on a switch used to do (the switch asks
  // first when anything is staged). Save and Discard drop it.
  const [money, setMoney, { clear: clearMoney }] = useDraftState<MoneyDraft>(`${draftBase}:money:${selectedCurrency}`, seedMoney)

  const moneyBase = seedMoney()
  // The per-seat price is hidden and inert under manual, so it never counts dirty.
  const moneyDirtyCount =
    (money.free.trim() !== moneyBase.free.trim() ? 1 : 0) +
    (money.extra.trim() !== moneyBase.extra.trim() ? 1 : 0) +
    (money.grace.trim() !== moneyBase.grace.trim() ? 1 : 0) +
    (provider && money.price.trim() !== moneyBase.price.trim() ? 1 : 0)

  // #1316 review round 2 (owner, 2026-10-28): every section's Save FINISHES as the identity that pressed it (`batch` —
  // the host bar's one batch, else its own); nothing is shown or reloaded once that session ended
  const saveMoney = async (batch: PinnedBatch = pinnedBatch()) => {
    // In bar mode a validation failure THROWS so the controller halts the batch
    // and reports; standalone it toasts. Same split `PlanPrices` used.
    const fail = (msg: string) => { const e = new Error(msg); if (bar) throw e; toast.error(msg) }
    const poolFree = inputToMicro(money.free)
    if (poolFree == null) return fail(t('catalog.plans.priceset.errPool', {
      defaultValue: 'Free credits for {{c}} must be a non-negative amount.', c: selectedCurrency.toUpperCase(),
    }))
    const poolExtra = inputToMicro(money.extra)
    if (poolExtra == null) return fail(t('catalog.plans.priceset.errPool', {
      defaultValue: 'Extra credits for {{c}} must be a non-negative amount.', c: selectedCurrency.toUpperCase(),
    }))
    const poolGrace = inputToMicro(money.grace)
    if (poolGrace == null) return fail(t('catalog.plans.priceset.errPool', {
      defaultValue: 'Grace credits for {{c}} must be a non-negative amount.', c: selectedCurrency.toUpperCase(),
    }))
    // Under manual the price field is hidden — preserve the stored value (the
    // real seat charge is the per-tenant contract; `amount-due.ts` reads
    // base_price_minor unconditionally for the preset rung, but it is 0 under
    // manual on the seeded catalog, so a new manual plan quotes 0/seat from the
    // rung until a per-tenant contract price is set. That is the manual seat
    // mechanism, not an under-charge bug.
    let priceMinor: number
    if (provider) {
      const p = parsePrice(money.price, selectedCurrency)
      if (p == null) return fail(t('catalog.plans.priceset.errPrice', {
        defaultValue: 'Price per extra seat for {{c}} is not a valid amount.', c: selectedCurrency.toUpperCase(),
      }))
      priceMinor = p
    } else {
      priceMinor = selectedRow?.basePriceMinor ?? 0
    }
    // FULL-ROW UPSERT — `setPlanPrice` writes the WHOLE money row, so every
    // save re-sends every column or the omitted one is ZEROED (Phase 4 trap).
    // `extra_seat_price` (vestigial) and `extra_seat_allowance` (retired) are
    // both written 0 here — the retirement folds the allowance into base_pool.
    await batch.run(() => catalogAdminApi.setPlanPrice(plan.id, selectedCurrency, {
      basePriceMinor: priceMinor,
      extraSeatPriceMinor: 0,
      basePoolBillingMicro: poolFree,
      extraSeatAllowanceBillingMicro: 0,
      extraPoolBillingMicro: poolExtra,
      gracePoolBillingMicro: poolGrace,
    }))
    if (batch.ended()) { clearMoney(); return }
    if (!bar) toast.success(t('catalog.plans.prices.saved', { defaultValue: 'Prices saved.' }))
    await onSaved()
    clearMoney()
  }
  const discardMoney = () => clearMoney()
  useRegisterSection(bar, 'prices', moneyDirtyCount, saveMoney, discardMoney)

  // Remove the SELECTED currency's price row (the affordance `PlanPrices` used
  // to own). Locked for the deployment's own billing currency while the plan is
  // active — that combination breaks `applyEntitlements` for every tenant on it.
  const removeLocked = selectedCurrency === bc && plan.isActive
  const canRemove = !!selectedRow && !removeLocked
  const removeSelectedCurrency = async () => {
    if (!canRemove) return
    const ok = await confirm({
      title: t('catalog.plans.priceset.removeTitle', {
        defaultValue: 'Remove {{c}} pricing?', c: selectedCurrency.toUpperCase(),
      }),
      message: t('catalog.plans.priceset.removeBody', {
        defaultValue: 'This plan will no longer be priced in {{c}}. Tenants are still charged in the deployment’s billing currency.',
        c: selectedCurrency.toUpperCase(),
      }),
      confirmText: t('catalog.plans.priceset.removeConfirm', { defaultValue: 'Remove currency' }),
      type: 'warning',
    })
    if (!ok) return
    try {
      await catalogAdminApi.removePlanPrice(plan.id, selectedCurrency)
      setSelectedCurrency(bc)
      await onSaved()
    } catch (e: any) {
      toast.error(e?.message || t('catalog.err.saveFailed'))
    }
  }

  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <label htmlFor={`priceset-currency-${plan.id}`} className="themed-text-secondary text-xs">
          {t('catalog.plans.priceset.selectorLabel', {
            defaultValue: 'Prices by',
          })}
        </label>
        <select
          id={`priceset-currency-${plan.id}`}
          value={selectedCurrency}
          disabled={busy}
          onChange={e => { void switchCurrency(e.target.value) }}
          aria-label={t('catalog.plans.priceset.selectorLabel', {
            defaultValue: 'Prices by',
          })}
          className="themed-input border rounded px-1.5 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
        >
          <optgroup label={t('catalog.plans.priceset.groupPriced', { defaultValue: 'Priced' })}>
            {pricedCurrencies.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </optgroup>
          {unpricedCurrencies.length > 0 && (
            <optgroup label={t('catalog.plans.priceset.groupSupported', { defaultValue: 'Supported, not yet priced' })}>
              {unpricedCurrencies.map(c => <option key={c} value={c}>{c.toUpperCase()}</option>)}
            </optgroup>
          )}
        </select>
        {selectedRow && (
          <button
            type="button"
            onClick={() => { void removeSelectedCurrency() }}
            disabled={busy || !canRemove}
            title={removeLocked
              ? t('catalog.plans.prices.billingLocked', { defaultValue: 'The billing currency cannot be removed while the plan is active.' })
              : undefined}
            aria-label={t('catalog.plans.priceset.removeCurrency', {
              defaultValue: 'Remove {{c}} pricing', c: selectedCurrency.toUpperCase(),
            })}
            className="text-[11px] themed-text-muted hover:themed-text underline disabled:opacity-40 disabled:no-underline"
          >
            {t('catalog.plans.prices.remove', { defaultValue: 'Remove' })}
          </button>
        )}
      </div>

      {isBillingCurrency && (
        <p className="themed-text-muted text-[11px] mb-2">
          <span className="mr-1 text-[10px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-300">
            {t('catalog.plans.prices.billing', { defaultValue: 'Billing' })}
          </span>
          {t('catalog.plans.priceset.loadBearing', {
            defaultValue: '{{c}} is this deployment’s billing currency — these are the prices tenants are actually charged.',
            c: selectedCurrency.toUpperCase(),
          })}
        </p>
      )}
      {isReserved && (
        <p className="themed-text-muted text-[11px] mb-2">
          {t('catalog.plans.priceset.reserved', {
            defaultValue: 'Reserved for other markets — {{c}} prices are stored but not charged on this deployment.',
            c: selectedCurrency.toUpperCase(),
          })}
        </p>
      )}

      {/* Row 6 — the selected currency's typed amounts: free credits, extra
          pool, grace pool, and (provider only) the per-extra-seat price. Each
          saves through `setPlanPrice`, re-sending the whole money row. */}
      <div className="flex flex-wrap items-end gap-4 mb-3">
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-xs flex items-center gap-1">
            {t('catalog.plans.col.freeCredits', { defaultValue: 'Free credits' })}
            <InfoHint text={t('catalog.plans.priceset.freeCreditsHint', {
              defaultValue: 'The included AI allowance for tenants on this plan, in {{c}}.',
              c: selectedCurrency.toUpperCase(),
            })} />
          </span>
          <input
            value={money.free}
            disabled={busy}
            onChange={e => setMoney(m => ({ ...m, free: e.target.value }))}
            inputMode="decimal"
            placeholder="0"
            aria-label={`${selectedCurrency.toUpperCase()} ${t('catalog.plans.col.freeCredits', { defaultValue: 'Free credits' })}`}
            className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-xs flex items-center gap-1">
            {t('catalog.plans.planExtraPool', { defaultValue: 'Extra credits' })}
            <InfoHint text={t('catalog.plans.priceset.extraPoolHint', {
              defaultValue: 'The postpaid overage ceiling seeded onto tenants on this plan, in {{c}}. 0 = no overage.',
              c: selectedCurrency.toUpperCase(),
            })} />
          </span>
          <input
            value={money.extra}
            disabled={busy}
            onChange={e => setMoney(m => ({ ...m, extra: e.target.value }))}
            inputMode="decimal"
            placeholder="0"
            aria-label={`${selectedCurrency.toUpperCase()} ${t('catalog.plans.planExtraPool', { defaultValue: 'Extra credits' })}`}
            className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="themed-text-secondary text-xs flex items-center gap-1">
            {t('catalog.plans.planGracePool', { defaultValue: 'Grace credits' })}
            <InfoHint text={t('catalog.plans.priceset.gracePoolHint', {
              defaultValue: 'The grace allowance seeded onto tenants on this plan, in {{c}} — clamped to the platform grace ceiling. 0 (or blank) = inherit the platform grace ceiling (grace is NOT disabled — to turn grace off entirely, set the platform grace ceiling to 0).',
              c: selectedCurrency.toUpperCase(),
            })} />
          </span>
          <input
            value={money.grace}
            disabled={busy}
            onChange={e => setMoney(m => ({ ...m, grace: e.target.value }))}
            inputMode="decimal"
            placeholder="0"
            aria-label={`${selectedCurrency.toUpperCase()} ${t('catalog.plans.planGracePool', { defaultValue: 'Grace credits' })}`}
            className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
          />
        </label>
        {provider && (
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.pricePerExtraSeat', { defaultValue: 'Price per extra seat' })}
              <InfoHint text={t('catalog.plans.priceset.pricePerExtraSeatHint', {
                defaultValue: 'What a member beyond the included seats is charged, per {{c}} billing cycle. This is the Stripe unit amount.',
                c: selectedCurrency.toUpperCase(),
              })} />
            </span>
            <input
              value={money.price}
              disabled={busy}
              onChange={e => setMoney(m => ({ ...m, price: e.target.value }))}
              inputMode="decimal"
              placeholder={t('catalog.plans.unpriced', { defaultValue: 'Unpriced' })}
              aria-label={`${selectedCurrency.toUpperCase()} ${t('catalog.plans.pricePerExtraSeat', { defaultValue: 'Price per extra seat' })}`}
              className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
        )}
      </div>

      {/* Row 7+8 — the PLAN rung of metered feature pricing (sandbox compute,
          web search — server-ordered via `PER_TENANT_PRICEABLE`). Blank
          inherits the deployment default (free). Renders BEFORE the AI models
          table (redesign). */}
      <FeaturePricingEditor scope="plan" scopeId={plan.id} currency={selectedCurrency} busy={busy} bar={bar} draftKey={`${draftBase}:metered`} t={t} />

      {/* Row 9 — the PLAN rung of the model-pricing ladder, carrying the model
          ALLOW-LIST in the same grid (an "Allowed" column). Blank price inherits
          the BUILT-IN default rate (providers.ts); the allow-list writes
          `features.models`. */}
      <LlmPricing
        busy={busy}
        t={t}
        scope="plan"
        scopeId={plan.id}
        currency={selectedCurrency}
        draftKey={`${draftBase}:llm`}
        title={t('catalog.plans.llm.planTitle', { defaultValue: 'AI models on this plan' })}
        desc={t('catalog.plans.llm.planDesc', {
          defaultValue:
            'Which AI models tenants on this plan may use, and the price they pay. Blank price inherits the built-in default rate.',
        })}
        allowedModels={(plan.features?.models as string[] | undefined) ?? null}
        onAllowedSave={async (m) => {
          // MERGE patch — touch only our own key. A whole-object replace from
          // this render's snapshot silently reverts whatever a sibling section
          // (uploads, ceilings) saved moments earlier under the same Save.
          // `null` deletes: empty ⇒ "includes everything".
          await catalogAdminApi.updatePlan(plan.id, {
            featuresPatch: { models: m && m.length > 0 ? m : null },
          })
          await onSaved()
        }}
        bar={bar}
      />
    </div>
  )
}

/**
 * `plan-create` (P2 Task 1) — the in-memory draft `<PlanEditor>` holds while a
 * new plan is being defined. Nothing here is persisted; Confirm (P2 Task 2)
 * is what turns a finished draft into `createPlan` + the price/model/metered
 * follow-up calls (design §5.3,
 * `docs/plans/2026-08-21_PER_CURRENCY_PRICING_AND_PLAN_EDITOR.md`).
 *
 * Mirrors the PLAN-scope scalar `PlanDraft` (`CatalogAdmin.tsx`) plus the two
 * `features` axes `PlanSavers` also covers (uploads / rate limits) and the
 * billing rhythm — everything a `PlanSavers` implementation can mutate.
 * `features` is a plain merge bag (like the real plan's `features` JSONB) so
 * `planBlocksCreate` / `resolvePlanRateLimit` read it identically to a
 * persisted plan's.
 *
 * **Now holds the FULL price set + model + metered drafts (0190 Phase 6,
 * Task 10).** `LlmPricing` and `FeaturePricingEditor` gained a controlled
 * DRAFT MODE (`draftValue` + `onDraftChange`) that seeds a blank grid from
 * the static catalog instead of fetching by a not-yet-existent `scopeId`, so
 * Rows 6–9 are editable in the blank Add modal exactly as in the plan editor.
 * `confirmDraft` persists them after the row is created. `PlanCycle` is still
 * NOT reused here — its own `save()` calls
 * `catalogAdminApi.planCycleImpact(plan.id)` **unconditionally** against a
 * synthetic id (a real network call that would answer "nobody" the hard
 * way), so the billing rhythm stays on the draft's `cycleCount`/`cycleUnit`
 * defaults until the real editor Confirm opens. The billing currency is shown
 * as a locked label (no selector — a plan can only ever be priced in it at
 * creation time, design §5.3).
 */
export interface PlanCreateDraft {
  name: string
  audience: 'personal' | 'organization' | 'both'
  storageBytes: number
  concurrency: number | null
  /** O7 relative compute share (#539) — see `PlanDraft.fairnessWeightText`. */
  fairnessWeightText: string
  includedSeatsText: string
  maxSeatsText: string
  cycleCount: number
  cycleUnit: 'day' | 'week' | 'month'
  /** Merge bag for `canCreateContent` (uploads) and `rateLimits` — the two
   *  `features` axes `PlanSavers` covers. Shaped like a real plan's
   *  `features` JSONB. */
  features: Record<string, unknown>
  /**
   * The full billing-currency PRICE SET, in the DEPLOYMENT BILLING CURRENCY
   * (there is no currency selector in create — a new plan can only ever be
   * priced in the billing currency at creation, design §5.3). 0190 Phase 6
   * (Task 10) gave the Add modal FULL parity with the plan-scope Row 6: the
   * price per extra seat (→ `base_price_minor`, provider only), free credits
   * (→ `base_pool`), extra pool and grace pool. Text like every other numeric
   * draft field — parsed on Confirm via `parsePrice`/`inputToMicro` (the
   * currency's own exponent is respected, never an assumed `/100`). `price`
   * and `freeCredits` are the two with NO platform default (Confirm gates on
   * them, provider-price only when provider-billed); `extra`/`grace` blank = 0.
   */
  pricePerSeatText: string
  freeCreditsText: string
  extraPoolText: string
  gracePoolText: string
  /**
   * Rows 7–9 draft state (0190 Phase 6, Task 10) — the metered feature rates
   * and the AI-model prices + allow-list, editable BEFORE the plan row exists
   * via the two editors' draft mode. Persisted by `confirmDraft` after the
   * plan is created. Billing currency only (create is single-currency).
   */
  meteredDraft: MeteredDraft
  modelDraft: LlmPricingDraft
}

/** Seeds a blank draft from the platform defaults. `name` is the one field
 *  NOT seeded from them — a plan states its own, so it starts blank and
 *  required, never a placeholder value an operator could miss. */
export function resolvePlanCreateDraft(defaults: PlanFieldDefaults): PlanCreateDraft {
  return {
    name: '',
    audience: 'both',
    storageBytes: defaults.storageQuotaBytes,
    concurrency: defaults.agentConcurrencyCap,
    // NOT from `defaults`: the fair-share weight has no platform-defaults
    // override surface (see `DEFAULT_PLAN_FAIRNESS_WEIGHT`'s own docblock), so a
    // new plan starts at the column's default — equal share — and differentiating
    // it is a deliberate act.
    fairnessWeightText: String(DEFAULT_PLAN_FAIRNESS_WEIGHT),
    includedSeatsText: String(defaults.includedSeats),
    maxSeatsText: '',
    cycleCount: 1,
    cycleUnit: 'month',
    features: {},
    pricePerSeatText: '',
    freeCreditsText: '',
    extraPoolText: '',
    gracePoolText: '',
    meteredDraft: {},
    modelDraft: { prices: {}, allowed: null },
  }
}

/**
 * `PlanSavers` for `plan-create` scope — mutates `draft` in React state
 * instead of calling `catalogAdminApi`. Injected into the same sub-editors
 * (`PlanUploads`, `PlanRateLimitsEditor`, and the scalar fields' own
 * `saveScalars`) that `plan` scope wires to `planSavers(p)`
 * (`CatalogAdmin.tsx`) — same seam, different target. Nothing here ever
 * reaches the network; Confirm (P2 Task 2) is what turns the finished draft
 * into real API calls.
 */
export function useDraftSavers(
  draft: PlanCreateDraft,
  setDraft: Dispatch<SetStateAction<PlanCreateDraft>>,
): PlanSavers {
  // `draft` itself is unused here (every mutator is a functional update) —
  // kept as a parameter per the brief's signature so a future caller that
  // wants to validate against the CURRENT draft before mutating it (e.g. a
  // duplicate-name check) has it without an API change.
  void draft
  return useMemo<PlanSavers>(() => ({
    async saveScalars(patch) {
      setDraft(d => ({
        ...d,
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.includedSeats !== undefined ? { includedSeatsText: String(patch.includedSeats) } : {}),
        ...(patch.storageBytes !== undefined ? { storageBytes: patch.storageBytes } : {}),
        ...(patch.concurrency !== undefined ? { concurrency: patch.concurrency } : {}),
        ...(patch.maxSeats !== undefined
          ? { maxSeatsText: patch.maxSeats == null ? '' : String(patch.maxSeats) }
          : {}),
      }))
      // No stale-entitlements to report — nothing is persisted yet for any
      // OTHER tenant to be stale against.
      return {}
    },
    async saveUploads(canCreate) {
      // Same merge-patch shape `planSavers(p).saveUploads` writes: `null`
      // deletes the key (absence is the unrestricted state), `false` pauses.
      setDraft(d => ({
        ...d,
        features: { ...d.features, canCreateContent: canCreate === false ? false : null },
      }))
    },
    async saveCycle(count, unit) {
      setDraft(d => ({ ...d, cycleCount: count, cycleUnit: unit }))
    },
    async saveRateLimits(limits) {
      setDraft(d => ({ ...d, features: { ...d.features, rateLimits: limits } }))
    },
    // Free/extra/grace caps and enforcement mode are tenant-only axes (Task 6,
    // P4) — a plan draft has no per-tenant override record and no enforcement
    // mode to stage. `tenantCaps`/`tenantEnforcement` never render for
    // `plan-create` scope, so these should never be called.
    async saveCaps() {
      throw new Error('saveCaps: not supported in plan-create scope — free/extra/grace are tenant-only axes')
    },
    async saveEnforcement() {
      throw new Error('saveEnforcement: not supported in plan-create scope — enforcement mode is a tenant-only axis')
    },
  }), [setDraft])
}

export interface PlanEditorProps {
  scope: PlanEditorScope
  savers: PlanSavers
  /** The single Save/Discard controller shared by every section registered
   *  below (`usePlanEditBar`, owned by the host). */
  bar: PlanEditBar
  busy: boolean
  /** 'manual' hides the extra-seat provider fields and the audience picker. */
  planAuthority: 'provider' | 'manual'
  /** Every ISO currency the deployment supports — `PriceSetPanel`'s selector. */
  currencies: string[]
  /** The deployment's own billing currency. */
  defaultCurrency: string
  /** The scalar-fields draft (name, seats, storage, concurrency, max seats)
   *  for the plan `scope` is showing. `undefined` while drafts have not been
   *  seeded yet — the fields render nothing until they are. */
  draft: PlanDraft | undefined
  onDraftChange: (patch: Partial<PlanDraft>) => void
  /** Re-fetch the catalog after any sub-editor's own save. */
  onSaved: () => Promise<void>
  /** Reset the scalar-fields draft to its last-loaded value — the scalar
   *  section's own `discard`, registered into `bar` alongside every other
   *  section's. The host owns the draft's pristine copy (`pBase` in
   *  `CatalogAdmin`), not this component, so it is a callback rather than a
   *  value computed here. Optional like the other host-owned callbacks below
   *  (meaningless for `plan-create`/`tenant`, which never reach the scalar
   *  section at all — see the early return in the body). */
  onDiscardScalars?: () => void
  /** Fired after the scalar section's own save succeeds, with whatever
   *  `savers.saveScalars` resolved — the host bubbles a grant-affecting
   *  change up to its own stale-entitlements banner / F3 auto-apply prompt.
   *  See `CatalogAdmin.tsx`'s `handleScalarSaved`. */
  onScalarSaved?: (result: PlanScalarSaveResult) => void
  /** `platform_settings.default_plan_id`. `undefined` hides the "Default"
   *  affordances entirely; `null` = no default configured. */
  defaultPresetId?: string | null
  onSetDefaultPreset?: (planId: string) => Promise<void>
  onPropagate?: (plan: { id: string; name: string }) => void
  renderPlanTenants?: (plan: { id: string; name: string }) => ReactNode
  /** Open with the Tenant-specific controls section pre-expanded. */
  expandTenants?: boolean
  /** The aggregate Save bar's own state — fed by the host's `usePlanEditBar`
   *  (`total`/`saveAll`/`discardAll`) plus its own in-flight flag. */
  saveBarTotal: number
  saveBarBusy: boolean
  onSaveAll: () => void
  onDiscardAll: () => void
  /**
   * `plan-create` scope ONLY (P2 Task 2) — fired with the server-minted plan
   * id once Confirm's persist sequence has created the row, whether the rest
   * of the sequence (price/features/activate) succeeded or failed partway.
   * The host opens the real (now-persisted) plan's Edit modal on it — design
   * §5.3: a mid-sequence failure leaves a real, editable, still-inactive plan
   * rather than a phantom, so the operator finishes or fixes it there. Never
   * fired if `createPlan` itself throws (no row exists to open).
   */
  onOpenPlan?: (planId: string) => void
  /**
   * `plan-create` scope ONLY — where a new plan lands in the display order.
   * `undefined` (host didn't wire it) falls back to `0`, same as every other
   * plan predating the redesign's sort column. The host computes
   * `max(existing) + 10` (see `PlansSection`'s call site) rather than this
   * component deriving it, because only the host has the live plan list —
   * `plan-create` scope's own draft has nothing to compare against.
   */
  nextSortOrder?: number
  /**
   * `plan-create` scope ONLY — fired whenever `confirmDraft`'s own in-flight
   * flag (`confirming`) flips, so the HOST's Modal shell can refuse to
   * dismiss (Escape / backdrop) while the create→price→activate sequence is
   * running. Without this the host only knows its OWN `busy` (disables the
   * dialog for unrelated page-level reasons — see `confirming`'s own
   * docblock), so a dismiss mid-Confirm used to unmount this component while
   * the persist sequence kept running to completion in the background — the
   * plan got created, priced and activated, and the edit modal opened, on a
   * dismissal the operator explicitly asked for. `Modal`'s own docblock
   * states this exact contract: `dismissible={false}` is how a dialog
   * mid-write refuses to vanish.
   */
  onConfirmingChange?: (confirming: boolean) => void
  /**
   * `tenant` scope ONLY (P4 Task 2) — `customers:write`. Governs the scalar
   * fields (storage/concurrency), uploads, request limits and the platform
   * model allow-list. Defaults `true` so `plan`/`plan-create` scope (which
   * never pass it) are unaffected — a platform admin editing the price book
   * already implies both grants below.
   */
  canEdit?: boolean
  /**
   * `tenant` scope ONLY — `pricing:write`, SEPARATE from `canEdit` on
   * purpose (P3 Task 4b ruling): the CYCLE control couples to the charged
   * amount, so it and the per-tenant pricing editors gate on the pricing
   * grant, not the general one. Defaults `true` for the same reason as
   * `canEdit` above.
   */
  canEditPricing?: boolean
  /**
   * `tenant` scope ONLY — commits `ModelAccessEditor`'s write for the
   * PLATFORM allow-list (`tenant_settings.allowed_models`). Not part of
   * `PlanSavers`: it has no `plan`/`plan-create` analog — a plan's own model
   * restriction is `features.models`, already covered by `LlmPricing`'s
   * `allowedModels` prop inside `PriceSetPanel` (unused here, see the
   * tenant branch below).
   */
  onSaveTenantAllowedModels?: (models: string[] | null) => Promise<void>
  /**
   * `tenant` scope ONLY — renders the resolved "what they can actually use"
   * view beneath the allow-list editor. A slot, not an import: the real
   * component (`CustomerModelAccess`) lives in `@papan/orgs`, which
   * `@papan/billing` may not depend on — same shape as `renderPlanTenants`
   * above and the launcher slot on `CustomerDetail`.
   */
  resolvedModelAccessSlot?: ReactNode
  /**
   * `tenant` scope ONLY — rendered beneath the storage-quota field (Task 7).
   * A slot, not an import: `StorageUse` reads `row.storageUsedBytes`, a
   * tenant-only figure with no plan analog, so `@papan/billing` cannot own
   * it — same shape as `resolvedModelAccessSlot` above.
   */
  storageUseSlot?: ReactNode
  t: T
}

export function PlanEditor({
  scope, savers, busy, planAuthority, currencies, defaultCurrency,
  draft: d, onDraftChange, onSaved, onDiscardScalars, onScalarSaved,
  defaultPresetId, onSetDefaultPreset,
  onPropagate, renderPlanTenants, expandTenants = false, bar,
  saveBarTotal, saveBarBusy, onSaveAll, onDiscardAll, onOpenPlan, nextSortOrder,
  onConfirmingChange, canEdit = true, canEditPricing = true,
  onSaveTenantAllowedModels, resolvedModelAccessSlot, storageUseSlot, t,
}: PlanEditorProps) {
  // ── HOOKS — called on EVERY render, regardless of `scope.kind` ──────────
  //
  // This used to be `if (scope.kind !== 'plan') return null` ABOVE
  // `useRegisterSection` (P1: safe only because no other scope ever reached
  // this component, so the hook count was trivially constant — 0 every time
  // for `plan-create`/`tenant`). P2 makes `plan-create` render real content,
  // which would have turned that early return into a genuine Rules-of-Hooks
  // violation: React keys hook state by CALL ORDER within one mounted
  // instance's render, and an early return before a hook call is a
  // conditional call — forbidden even though, in practice, a given
  // `<PlanEditor>` instance's `scope.kind` never changes across its own
  // renders (the rule is enforced structurally, not "did this instance
  // actually vary"). Every hook below now runs unconditionally; only the
  // RENDER at the bottom branches on `scope.kind`.
  const isPlanScope = scope.kind === 'plan'
  const p = isPlanScope ? scope.plan : undefined

  // `plan-create`'s own in-memory draft (P2 Task 1). Seeded once, from this
  // scope's defaults — lazy initializer, so it costs nothing on renders where
  // `scope.kind !== 'plan-create'`. `resolvePlanFieldDefaults()` (no `stored`
  // override) is a harmless, pure fallback for the initializer's OTHER
  // branches — never rendered, since only `plan-create`'s JSX below ever
  // reads `createDraft`.
  // Kept across a phone/desktop layout swap (#897) while this is the
  // `plan-create` scope; the seed is computed once (stable identity — the draft
  // editors below take its nested objects as props).
  const [createSeed] = useState<PlanCreateDraft>(() =>
    resolvePlanCreateDraft(scope.kind === 'plan-create' ? scope.defaults : resolvePlanFieldDefaults()),
  )
  const [createDraft, setCreateDraft] = useDraftState<PlanCreateDraft>(
    scope.kind === 'plan-create' ? 'planCreate:new' : null, createSeed,
  )
  const createSavers = useDraftSavers(createDraft, setCreateDraft)
  // `plan-create`'s own Confirm in-flight flag — separate from `busy` (host-
  // owned, disables the whole dialog for OTHER reasons) so this instance can
  // disable its own Confirm button without the host knowing anything ran.
  const [confirming, setConfirmingState] = useState(false)
  // Sets the local flag AND bubbles it to the host (see `onConfirmingChange`'s
  // own docblock) — every call site below goes through this, never
  // `setConfirmingState` directly, so the two can never drift apart.
  const setConfirming = (v: boolean) => { setConfirmingState(v); onConfirmingChange?.(v) }
  // #897 — the persist sequence is also marked in flight in the draft store:
  // a layout swap mid-sequence restores this form filled in, and the screen it
  // mounts must not offer Confirm (a second `createPlan` is the `-2` duplicate
  // `createdIdRef` exists to prevent — that ref died with the old screen).
  // How it ended reaches the HOST (`CatalogAdmin`'s plans list, which outlives
  // this form): the old screen's `onOpenPlan` / reload went to the old host.
  const [createInFlight, trackCreate] = useDraftInFlight<string | null>(scope.kind === 'plan-create' ? 'planCreate:new' : null)
  const submitting = confirming || createInFlight
  // BOTH edges to the host's close guard: on a restored form only this tells
  // it the old screen's request ended (its `setConfirming(false)` went to the
  // old host), and a guard left true keeps every later create dialog shut.
  useEffect(() => { onConfirmingChange?.(confirming || createInFlight) }, [createInFlight]) // eslint-disable-line react-hooks/exhaustive-deps
  const toast = useToast()
  // Fix-round 1 — guards a SECOND Confirm click after `createPlan` already
  // succeeded. `setConfirming(false)` in the `finally` below re-enables the
  // button once the sequence settles (success OR a post-create failure), and
  // the draft is never cleared — Task 3's host wiring (closing/unmounting the
  // form on success) doesn't exist yet, so nothing else stops a re-click from
  // reaching this handler while `createdIdRef` is already set. Without this,
  // a re-click re-runs `createPlan` and mints a slug-suffixed DUPLICATE (`-2`)
  // of the plan just created. A `ref`, not state: it must be readable
  // synchronously at the top of the very next invocation, and setting it
  // must not itself trigger a render.
  const createdIdRef = useRef<string | null>(null)

  /**
   * P2 Task 2 — the Confirm persist sequence (design §5.3). Runs ONCE, in
   * order: create the row INACTIVE → price it → apply the uploads/rate-limits
   * features the operator changed on the draft → activate LAST, only once the
   * plan is fully priced (`billing_plans.is_active` defaults `true`, and an
   * active-but-unpriced plan is the exact state `catalog-routes.ts:907-909` /
   * `PlanNotPricedError` treat as illegal).
   *
   * A no-op for any scope other than `plan-create` — only that scope's JSX
   * ever calls it, but the guard keeps it safe to define unconditionally
   * (rules-of-hooks: no hooks below this point, so no ordering hazard, but
   * `scope.kind` narrowing still needs the check for TypeScript).
   */
  const confirmDraft = async () => {
    if (scope.kind !== 'plan-create') return
    // Already created (a prior click got past `createPlan`, whether the rest
    // of the sequence then succeeded or failed) — re-clicking must NOT create
    // a second row. Just re-open the real editor for the one that exists.
    if (createdIdRef.current) {
      onOpenPlan?.(createdIdRef.current)
      return
    }
    if (isDraftInFlight('planCreate:new')) return
    const cd = createDraft
    const provider = planAuthority === 'provider'
    const name = cd.name.trim()
    if (!name) {
      toast.error(t('catalog.err.name', { id: name }))
      return
    }
    // O7 compute share (#539). Gated BEFORE the row is minted, like every
    // other numeric on this dialog: the column's CHECK would otherwise refuse
    // mid-sequence, after `createPlan` has already been attempted.
    const fairnessWeight = parseOptionalInt(cd.fairnessWeightText)
    if (
      typeof fairnessWeight !== 'number'
      || fairnessWeight < PLAN_FAIRNESS_WEIGHT_MIN
      || fairnessWeight > PLAN_FAIRNESS_WEIGHT_MAX
    ) {
      toast.error(t('catalog.err.fairnessWeight', {
        defaultValue: '{{id}}: compute share must be a whole number between {{min}} and {{max}}.',
        id: name, min: PLAN_FAIRNESS_WEIGHT_MIN, max: PLAN_FAIRNESS_WEIGHT_MAX,
      }))
      return
    }
    // Price per extra seat → `base_price_minor`, provider only. Under manual
    // the field is hidden and a new plan quotes 0/seat from the preset rung
    // until a per-tenant contract price is set (the manual seat mechanism,
    // not an under-charge bug — see `PriceSetPanel`'s Row-6 note).
    let priceMinor = 0
    if (provider) {
      const p = parsePrice(cd.pricePerSeatText, defaultCurrency)
      if (p == null) {
        toast.error(t('catalog.err.price', { id: name }))
        return
      }
      priceMinor = p
    }
    const pool = inputToMicro(cd.freeCreditsText)
    if (pool == null) {
      toast.error(t('catalog.err.freeCredits', {
        defaultValue: 'Enter the free credits (a non-negative amount).',
      }))
      return
    }
    // Extra/grace pools: blank = 0, a non-blank value must parse to a
    // non-negative amount. Note the two 0-semantics differ downstream: extra 0
    // = overage disabled; grace 0 ⇒ NULL = inherit the platform grace ceiling
    // (NOT grace-off — see the gracePoolHint).
    const extraPool = cd.extraPoolText.trim() === '' ? 0 : inputToMicro(cd.extraPoolText)
    if (extraPool == null) {
      toast.error(t('catalog.err.extraPool', { defaultValue: 'Extra credits must be a non-negative amount.' }))
      return
    }
    const gracePool = cd.gracePoolText.trim() === '' ? 0 : inputToMicro(cd.gracePoolText)
    if (gracePool == null) {
      toast.error(t('catalog.err.gracePool', { defaultValue: 'Grace credits must be a non-negative amount.' }))
      return
    }
    // Validate the Rows 7–9 drafts BEFORE anything is created — a
    // present-but-unparseable metered rate or model price ABORTS the whole
    // Confirm (no plan row is minted), the SAME rigor the edit-mode editors'
    // `save()` applies via the shared `isValidRateField` /
    // `modelPriceFieldStatus`. A blank field is "inherit", not invalid — it is
    // skipped, never a reason to abort. Without this a fat-fingered override
    // was silently dropped and the plan created + activated at the default
    // rate: a possible under-charge with no error shown.
    for (const [feature, dr] of Object.entries(cd.meteredDraft)) {
      if (!isValidRateField(dr.rate)) {
        toast.error(t('catalog.metered.err.rate', {
          defaultValue: 'Rate for {{f}} is not a valid amount',
          f: t(`catalog.metered.feature.${feature}`, { defaultValue: feature.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) }),
        }))
        return
      }
    }
    for (const [modelId, pr] of Object.entries(cd.modelDraft.prices)) {
      if (modelPriceFieldStatus(pr.input, pr.output) === 'invalid') {
        toast.error(t('catalog.plans.llm.bad', {
          defaultValue: '{{model}}: both prices must be zero or more, or both blank to remove the override.',
          model: modelId,
        }))
        return
      }
    }

    await trackCreate(() => persistCreate(cd, name, priceMinor, pool, extraPool, gracePool, fairnessWeight))
  }

  const persistCreate = async (
    cd: typeof createDraft, name: string, priceMinor: number, pool: number,
    extraPool: number, gracePool: number, fairnessWeight: number,
  ): Promise<string | null> => {
    setConfirming(true)
    // #1316 review round 2 (owner, 2026-10-28): the create the person confirmed FINISHES as the identity that confirmed
    // it — every step below is sent as it (`batch.run`), never as the next session; nothing is shown once that ended
    const batch = pinnedBatch()
    // Resolves with the created plan's id (null: nothing was created) — what
    // a host that replaced this screen in a swap opens (`useDraftInFlight`).
    // ── 1. CREATE THE ROW, INACTIVE ──────────────────────────────────────
    // A createPlan failure leaves nothing behind — stay on the draft so the
    // operator can retry without risking a duplicate slug.
    let createdId: string
    try {
      const { plan } = await batch.run(() => catalogAdminApi.createPlan({
        name,
        isActive: false,
        includedSeats: parseOptionalInt(cd.includedSeatsText) ?? 0,
        // The provider seat ceiling, set as "Max additional seats" on Row 1
        // (absolute `max_seats` on the draft). `null` = unlimited.
        maxSeats: parseOptionalInt(cd.maxSeatsText) ?? null,
        storageQuotaBytes: cd.storageBytes,
        agentConcurrencyCap: cd.concurrency,
        fairnessWeight,
        audience: cd.audience,
        cycleCount: cd.cycleCount,
        cycleUnit: cd.cycleUnit,
        // `max(existing) + 10`, from the host (see `nextSortOrder`'s own
        // docblock) — never a bare `0`, which would undo the deliberate
        // "leave gaps to slot a plan in later" ordering every other plan on
        // the catalogue already has.
        sortOrder: nextSortOrder ?? 0,
      }))
      createdId = plan.id
      // Stamped IMMEDIATELY on success, before pricing even starts — a
      // re-click racing the rest of this sequence must see the row already
      // exists, not just a click racing a resolved promise.
      createdIdRef.current = createdId
    } catch (e: any) {
      if (!batch.ended()) toast.error(e?.message || t('catalog.err.saveFailed'))
      setConfirming(false)
      return null
    }

    // ── 2+. PRICE IT (FULL money row) → metered → models+allow-list →
    //        uploads/rate-limits → ACTIVATE LAST ──────────────────────────
    // From here the row exists — any failure below is reported and the
    // operator is handed the plan's real (still-inactive) editor to finish,
    // never retried from the top (that would mint a duplicate).
    try {
      // FULL-ROW UPSERT — every column or the omitted one is zeroed (Phase 4
      // trap). `extra_seat_price` (vestigial) and `extra_seat_allowance`
      // (retired) are 0; the free-cap folds into `base_pool`.
      await batch.run(() => catalogAdminApi.setPlanPrice(createdId, defaultCurrency, {
        basePriceMinor: priceMinor,
        extraSeatPriceMinor: 0,
        basePoolBillingMicro: pool,
        extraSeatAllowanceBillingMicro: 0,
        extraPoolBillingMicro: extraPool,
        gracePoolBillingMicro: gracePool,
      }))
      // Metered feature pricing (Rows 7–8 draft). Per feature, mirror
      // `FeaturePricingEditor.save`'s per-axis inheritance: a blank rate ⇒
      // inherit the rate (`null`), `chargeable === false` ⇒ inherit the flag.
      // Both inheriting ⇒ nothing to write (the plan inherits the default).
      // Every present rate is already `isValidRateField` (validated above),
      // so no unusable rate can reach here.
      for (const [feature, dr] of Object.entries(cd.meteredDraft)) {
        const rateBlank = dr.rate.trim() === ''
        const chargeInherit = dr.chargeable === false
        if (rateBlank && chargeInherit) continue
        await batch.run(() => catalogAdminApi.setPlanFeaturePricing(createdId, feature, {
          chargeable: chargeInherit ? null : dr.chargeable,
          baseRateUsd: rateBlank ? null : Math.round(Number(dr.rate) * 1_000_000),
          currency: defaultCurrency,
        }))
      }
      // Model prices (Row 9 draft), same status rule as `LlmPricing.save`:
      // 'blank' ⇒ inherit the built-in rate (skip); 'valid' ⇒ write (an empty
      // side reads as 0, matching edit mode); 'invalid' was already aborted
      // above (the `!== 'valid'` guard is defensive).
      for (const [modelId, pr] of Object.entries(cd.modelDraft.prices)) {
        if (modelPriceFieldStatus(pr.input, pr.output) !== 'valid') continue
        await batch.run(() => catalogAdminApi.setModelPricing({
          scope: 'plan', scopeId: createdId, modelId, currency: defaultCurrency,
          inputUsdMicroPer1m: Math.round(Number(pr.input) * 1_000_000),
          outputUsdMicroPer1m: Math.round(Number(pr.output) * 1_000_000),
        }))
      }
      // The plan's model ALLOW-LIST (`features.models`) — a restriction only
      // when a non-empty subset is chosen; unrestricted (`null`) writes
      // nothing (absence = includes everything).
      const models = cd.modelDraft.allowed && cd.modelDraft.allowed.length > 0 ? cd.modelDraft.allowed : null
      if (models) {
        await batch.run(() => catalogAdminApi.updatePlan(createdId, { featuresPatch: { models } }))
      }
      // Uploads/rate-limits — whatever the operator changed on the draft via
      // `createSavers`. Empty (nothing touched) sends nothing: an empty
      // `featuresPatch` would still be a no-op server-side, but skipping the
      // call entirely keeps the persist sequence's network calls matched to
      // what actually changed.
      if (Object.keys(cd.features).length > 0) {
        await batch.run(() => catalogAdminApi.updatePlan(createdId, { featuresPatch: cd.features }))
      }
      await batch.run(() => catalogAdminApi.updatePlan(createdId, { isActive: true }))
      if (!batch.ended()) toast.success(t('catalog.toast.planCreated', { id: createdId }))
    } catch (e: any) {
      if (!batch.ended()) toast.error(e?.message || t('catalog.err.createdUnpriced', {
        defaultValue: 'Plan created, but pricing failed — set the price in the editor. ({{err}})',
        err: e?.message || '',
      }))
    } finally {
      if (!batch.ended()) {
        await onSaved()
        onOpenPlan?.(createdId)
      }
      setConfirming(false)
    }
    return createdId
  }

  // ── The scalar fields' own section (P1 Task 3) — `plan` scope ONLY ──────
  //
  // Dirty check mirrors `PlansSection`'s old `!same(pDrafts[id], pBase[id])`
  // — `planDraft(p)` IS what seeded `pBase[id]`, so comparing against it
  // live is the same test without needing that snapshot here. Guarded by
  // `isPlanScope`/`p` so this evaluates to "not dirty" / "no-op" for every
  // other scope rather than touching `p!.id` unsafely.
  const scalarDirty = isPlanScope && !!p && !!d && JSON.stringify(d) !== JSON.stringify(planDraft(p))
  // Not memoized: `useRegisterSection` reads `save`/`discard` through refs it
  // updates on every render (see its own docblock), so a fresh closure here
  // is deliberate, not a missed dependency.
  const saveScalars = async (batch: PinnedBatch = pinnedBatch()) => {
    if (!isPlanScope || !p || !d) return
    // Mirrors `savePlans`' old validation exactly (moved, not redesigned).
    if (!d.name.trim()) throw new Error(t('catalog.err.name', { id: p.id }))
    if (!/^\d+$/.test(d.includedSeatsText.trim())) throw new Error(t('catalog.err.includedSeats', { id: p.id }))
    const maxSeats = parseOptionalInt(d.maxSeatsText)
    if (maxSeats === undefined) throw new Error(t('catalog.err.maxSeats', { id: p.id }))
    if (parseOptionalInt(d.sortText) === undefined) throw new Error(t('catalog.err.sort', { id: p.id }))
    // Mirrors `billing_plans_fairness_weight_range` (0210) and the route's own
    // `intField(..., { min, max })`, so a bad value is refused here rather than
    // reaching Postgres.
    const fairnessWeight = parseOptionalInt(d.fairnessWeightText)
    if (
      typeof fairnessWeight !== 'number'
      || fairnessWeight < PLAN_FAIRNESS_WEIGHT_MIN
      || fairnessWeight > PLAN_FAIRNESS_WEIGHT_MAX
    ) {
      throw new Error(t('catalog.err.fairnessWeight', {
        defaultValue: '{{id}}: compute share must be a whole number between {{min}} and {{max}}.',
        id: p.id, min: PLAN_FAIRNESS_WEIGHT_MIN, max: PLAN_FAIRNESS_WEIGHT_MAX,
      }))
    }

    const res = await savers.saveScalars({
      name: d.name.trim(),
      includedSeats: parseInt(d.includedSeatsText.trim(), 10),
      storageBytes: d.storageBytes,
      concurrency: d.concurrency,
      maxSeats: maxSeats ?? null,
    }, batch)
    // audience / sortOrder / isActive / fairnessWeight are NOT in
    // `PlanScalarPatch` — they are catalog-plan-only concepts the tenant scope
    // (P4) has no equivalent of (a tenant does not have its own audience, sort
    // position, active flag, or fair-share weight: the weight is a property of
    // the PLAN, resolved live per tenant from it), so they stay a direct write
    // here rather than going through the injected saver.
    //
    // Safe to resend sortOrder/isActive unconditionally: neither is ever edited
    // by this dialog (see the comment below the max-seats field), so the value
    // sent always matches what is already stored. `audience` and
    // `fairnessWeight` ARE editable here, so this call is a real write for both.
    await batch.run(() => catalogAdminApi.updatePlan(p.id, {
      audience: d.audience,
      sortOrder: parseOptionalInt(d.sortText) ?? 0,
      isActive: d.isActive,
      fairnessWeight,
    }))
    if (batch.ended()) return
    // Reload FIRST, then drop the staged scalars (#897: they are a draft over the
    // plan): clearing before the reload showed the old values for one fetch.
    await onSaved()
    onScalarSaved?.(res)
  }
  useRegisterSection(bar, 'scalar', scalarDirty ? 1 : 0, saveScalars, () => onDiscardScalars?.())

  // ── `tenant` scope's OWN scalar section (P4 Task 2) — storage + concurrency
  // overrides only. Deliberately NOT routed through the host-owned `d`/
  // `onDraftChange` pair above (that is the PLAN-shaped scalar draft, gated
  // `isPlanScope`) — this holds only the STAGED DELTA, mirroring the exact
  // pattern `PlanUploads`'s own `drafts` state already uses: the BASELINE is
  // read from `scope` fresh on every render (never frozen at mount), and
  // local state carries only what the operator has actually touched. That is
  // what makes a Save-bar-less external reload (a sibling admin's edit, or
  // `useRefreshWhenShown` firing while this section itself is clean) show the
  // new value immediately rather than the stale one a mount-seeded `useState`
  // would freeze — see CLAUDE.md's "Keep-alive decks do not remount".
  //
  // #897 — the staged deltas below (scalars, caps, enforcement) are kept across
  // a phone/desktop layout swap under `customer:<tenant>:…`; their baselines
  // stay live, so nothing about a reload changes.
  const tenantDraftBase = scope.kind === 'tenant' ? `customer:${scope.tenantId}` : null
  const [tenantScalarDrafts, setTenantScalarDrafts, { clear: clearTenantScalarDrafts }] = useDraftState<{
    storageBytes?: number
    concurrency?: number | null
    seat?: number | null
    /** The seat mode `seat` was typed under — see the async-flip guard below. */
    seatMode?: 'included' | 'max' | 'none'
  }>(tenantDraftBase && `${tenantDraftBase}:scalars`, NO_DRAFT)
  const tenantBaseline = scope.kind === 'tenant'
    ? { storageBytes: scope.storageQuotaBytes, concurrency: scope.agentConcurrencyCap, seat: scope.seat.saved }
    : null
  const tenantStorageValue = tenantScalarDrafts.storageBytes !== undefined
    ? tenantScalarDrafts.storageBytes
    : (tenantBaseline?.storageBytes ?? 0)
  const tenantConcurrencyValue = 'concurrency' in tenantScalarDrafts
    ? (tenantScalarDrafts.concurrency ?? null)
    : (tenantBaseline?.concurrency ?? null)
  // Seat async-flip guard (M1/G1) — `scope.seat.mode` is derived from
  // `row.type` + `manualBilling`, and `manualBilling` resolves ASYNCHRONOUSLY
  // in `CustomerDetail.tsx`. A staged seat value counts ONLY under the mode it
  // was typed in (keyed on the DERIVED mode, never `manualBilling` directly), so
  // a value typed into a control that then disappears (included ⇄ max ⇄ none)
  // is never written to an axis the operator can no longer see.
  //
  // This replaced an effect that DELETED the staged seat on every mode change:
  // the screen a layout swap mounts (#897) starts with `manualBilling` false
  // and flips once it resolves — that flip would have wiped the carried seat.
  const tenantSeatMode = scope.kind === 'tenant' ? scope.seat.mode : undefined
  const tenantSeatStaged = 'seat' in tenantScalarDrafts && tenantScalarDrafts.seatMode === tenantSeatMode
  const tenantSeatValue = tenantSeatStaged
    ? (tenantScalarDrafts.seat ?? null)
    : (tenantBaseline?.seat ?? null)
  const tenantScalarsDirty = !!tenantBaseline && (
    (tenantScalarDrafts.storageBytes !== undefined && tenantScalarDrafts.storageBytes !== tenantBaseline.storageBytes)
    || ('concurrency' in tenantScalarDrafts && tenantScalarDrafts.concurrency !== tenantBaseline.concurrency)
    || (tenantSeatStaged && tenantScalarDrafts.seat !== tenantBaseline.seat)
  )
  const saveTenantScalars = async (batch: PinnedBatch = pinnedBatch()) => {
    if (scope.kind !== 'tenant') return
    const patch: PlanScalarPatch = {}
    if (tenantScalarDrafts.storageBytes !== undefined) patch.storageBytes = tenantScalarDrafts.storageBytes
    if ('concurrency' in tenantScalarDrafts) patch.concurrency = tenantScalarDrafts.concurrency ?? null
    // `seat` maps onto whichever axis `scope.seat.mode` names — the ONLY
    // seat control actually on screen, since the two are mutually exclusive
    // (Task 6's `mode` union). See the async-flip guard below for why the
    // draft key is cleared whenever `mode` itself changes.
    if (tenantSeatStaged) {
      const v = tenantScalarDrafts.seat ?? null
      if (scope.seat.mode === 'included') patch.includedSeats = v
      else if (scope.seat.mode === 'max') patch.maxSeats = v
    }
    if (Object.keys(patch).length === 0) return
    await savers.saveScalars(patch, batch)
    clearTenantScalarDrafts()
    if (!batch.ended()) await onSaved()
  }
  useRegisterSection(bar, 'tenantScalars', tenantScalarsDirty ? 1 : 0, saveTenantScalars, clearTenantScalarDrafts)

  // ── `tenant` scope's free/extra/grace caps (Task 7) — ONE multi-axis save
  // via `savers.saveCaps`, mirroring `tenantScalars`' own batching so this
  // section never races two `setTenantOverrides` calls against each other.
  // `undefined` = untouched; `null` = clear the override back to the plan.
  const [tenantCapsDraft, setTenantCapsDraft, { clear: clearTenantCapsDraft }] =
    useDraftState<{ free?: number | null; extra?: number | null; grace?: number | null }>(tenantDraftBase && `${tenantDraftBase}:caps`, NO_DRAFT)
  const tenantCapsBaseline = scope.kind === 'tenant'
    ? { free: scope.freeCap.saved, extra: scope.extraCap.saved, grace: scope.graceCap.saved }
    : null
  const tenantFreeCapValue = 'free' in tenantCapsDraft ? (tenantCapsDraft.free ?? null) : (tenantCapsBaseline?.free ?? null)
  const tenantExtraCapValue = 'extra' in tenantCapsDraft ? (tenantCapsDraft.extra ?? null) : (tenantCapsBaseline?.extra ?? null)
  const tenantGraceCapValue = 'grace' in tenantCapsDraft ? (tenantCapsDraft.grace ?? null) : (tenantCapsBaseline?.grace ?? null)
  const tenantCapsDirtyCount = tenantCapsBaseline
    ? (['free', 'extra', 'grace'] as const).filter(
        k => k in tenantCapsDraft && tenantCapsDraft[k] !== tenantCapsBaseline[k],
      ).length
    : 0
  const saveTenantCaps = async (batch: PinnedBatch = pinnedBatch()) => {
    if (scope.kind !== 'tenant') return
    const patch: { free?: number | null; extra?: number | null; grace?: number | null } = {}
    if ('free' in tenantCapsDraft) patch.free = tenantCapsDraft.free ?? null
    if ('extra' in tenantCapsDraft) patch.extra = tenantCapsDraft.extra ?? null
    if ('grace' in tenantCapsDraft) patch.grace = tenantCapsDraft.grace ?? null
    if (Object.keys(patch).length === 0) return
    await savers.saveCaps(patch, batch)
    clearTenantCapsDraft()
    if (!batch.ended()) await onSaved()
  }
  useRegisterSection(canEdit ? bar : undefined, 'tenantCaps', tenantCapsDirtyCount, saveTenantCaps, clearTenantCapsDraft)

  // ── `tenant` scope's enforcement mode (Task 7) — a plain scalar with no
  // plan/inheritance layer, so it is a bare `<select>`, not an
  // `InheritedCapField`, and has no reset affordance.
  const [tenantEnforcementDraft, setTenantEnforcementDraft, { clear: clearTenantEnforcementDraft }] =
    useDraftState<'enforce' | 'warn_only' | undefined>(tenantDraftBase && `${tenantDraftBase}:enforcement`, undefined)
  const tenantEnforcementDirty = scope.kind === 'tenant'
    && tenantEnforcementDraft !== undefined
    && tenantEnforcementDraft !== scope.enforcementMode
  const saveTenantEnforcement = async (batch: PinnedBatch = pinnedBatch()) => {
    if (scope.kind !== 'tenant' || tenantEnforcementDraft === undefined) return
    await savers.saveEnforcement(tenantEnforcementDraft, batch)
    clearTenantEnforcementDraft()
    if (!batch.ended()) await onSaved()
  }
  useRegisterSection(
    canEdit ? bar : undefined, 'tenantEnforcement',
    tenantEnforcementDirty ? 1 : 0, saveTenantEnforcement, clearTenantEnforcementDraft,
  )

  // ── RENDER — branches on `scope.kind`; every hook above already ran. ────
  if (scope.kind === 'plan-create') {
    const cd = createDraft
    const showAudienceCreate = planAuthority === 'provider'
    const providerCreate = planAuthority === 'provider'
    // "Max additional seats" (provider only), same absolute-`max_seats`
    // storage + recompute semantics as the plan-scope Row-1 field: shown as
    // seats PAST the included count, blank = unlimited.
    const cIncludedNum = parseOptionalInt(cd.includedSeatsText)
    const cIncludedSafe = typeof cIncludedNum === 'number' ? cIncludedNum : 0
    const cMaxSeatsNum = parseOptionalInt(cd.maxSeatsText)
    const cAdditionalValue = typeof cMaxSeatsNum === 'number'
      ? String(Math.max(0, cMaxSeatsNum - cIncludedSafe))
      : ''
    const onCreateAdditionalSeats = (text: string) => {
      const add = parseOptionalInt(text)
      if (add === null) { setCreateDraft(cur => ({ ...cur, maxSeatsText: '' })); return } // blank ⇒ unlimited
      if (typeof add === 'number') setCreateDraft(cur => ({ ...cur, maxSeatsText: String(cIncludedSafe + add) }))
      // invalid (undefined) ⇒ drop the keystroke (numeric field)
    }
    const onCreateFairnessWeight = (text: string) => {
      // Blank is allowed WHILE TYPING (a box you cannot clear cannot be
      // retyped); Confirm is what refuses an empty or out-of-range value.
      if (text === '' || /^\d{1,3}$/.test(text)) setCreateDraft(cur => ({ ...cur, fairnessWeightText: text }))
      // anything else ⇒ drop the keystroke (numeric field)
    }
    const onCreateIncludedSeats = (text: string) => {
      // Keep the operator's stated ADDITIONAL across included-seats edits by
      // recomputing the absolute ceiling (provider only, and only when a
      // ceiling is set) — mirrors the plan-scope `onIncludedSeatsChange`.
      if (!providerCreate || typeof cMaxSeatsNum !== 'number') {
        setCreateDraft(cur => ({ ...cur, includedSeatsText: text })); return
      }
      const additional = Math.max(0, cMaxSeatsNum - cIncludedSafe)
      const nextIncluded = parseOptionalInt(text)
      setCreateDraft(cur => typeof nextIncluded === 'number'
        ? { ...cur, includedSeatsText: text, maxSeatsText: String(nextIncluded + additional) }
        : { ...cur, includedSeatsText: text })
    }
    // A plan-SHAPED view of the draft, for the two sub-editors below that
    // still expect an `AdminPlanView` (`.id`/`.name`/`.features` are all they
    // read) — never sent anywhere. `createSavers` (built above) is what
    // actually reaches `createDraft` state; this is only what lets two
    // already scope/scopeId-free components render against a plan that has
    // no real id yet. `name` falls back to a placeholder so their own labels
    // ("<name> Allow uploads") don't read as a bare leading space while the
    // real name field is still blank.
    const draftAsPlan: AdminPlanView = {
      id: 'draft',
      name: cd.name || t('catalog.plans.addTitle', { defaultValue: 'New plan' }),
      audience: cd.audience,
      prices: [],
      includedSeats: parseOptionalInt(cd.includedSeatsText) ?? 0,
      maxSeats: parseOptionalInt(cd.maxSeatsText) ?? null,
      storageQuotaBytes: cd.storageBytes,
      agentConcurrencyCap: cd.concurrency,
      fairnessWeight: parseOptionalInt(cd.fairnessWeightText) || DEFAULT_PLAN_FAIRNESS_WEIGHT,
      features: cd.features,
      isActive: false,
      sortOrder: 0,
      cycleCount: cd.cycleCount,
      cycleUnit: cd.cycleUnit,
      assignedTenantCount: 0,
    }

    return (
      <div className="px-5 py-4">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.name', { defaultValue: 'Name' })}
            </span>
            <input
              value={cd.name}
              required
              disabled={busy}
              onChange={e => setCreateDraft(cur => ({ ...cur, name: e.target.value }))}
              aria-label={t('catalog.plans.col.name', { defaultValue: 'Name' })}
              aria-required="true"
              className="themed-input border rounded px-1.5 py-1 text-xs w-40 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          {showAudienceCreate && (
            <label className="flex flex-col gap-1">
              <span className="themed-text-secondary text-xs flex items-center gap-1">
                {t('catalog.plans.col.audience')}
                <InfoHint text={t('catalog.plans.audienceHint', {
                  defaultValue: 'Which tenant type this plan is offered to. Only affects the customer plan picker and provider upsell — inert under manual billing.',
                })} />
              </span>
              <select
                value={cd.audience}
                disabled={busy}
                onChange={e => setCreateDraft(cur => ({ ...cur, audience: e.target.value as PlanCreateDraft['audience'] }))}
                aria-label={t('catalog.plans.col.audience')}
                className="themed-input border rounded px-1 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
              >
                {['both', 'personal', 'organization'].map(a => (
                  <option key={a} value={a}>{t(`catalog.audience.${a}`)}</option>
                ))}
              </select>
            </label>
          )}
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.includedSeats', { defaultValue: 'Included seats' })}
              <InfoHint text={t('catalog.plans.includedSeatsHint', {
                defaultValue: 'Seats the plan provisions. Under manual billing this IS the seat ceiling; under provider billing, members beyond it are charged per extra seat.',
              })} />
            </span>
            <input
              value={cd.includedSeatsText}
              disabled={busy}
              onChange={e => onCreateIncludedSeats(e.target.value)}
              inputMode="numeric"
              aria-label={t('catalog.plans.col.includedSeats', { defaultValue: 'Included seats' })}
              className="themed-input border rounded px-1.5 py-1 text-xs w-20 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.storage', { defaultValue: 'Storage quota' })}
              <InfoHint text={t('catalog.plans.storageHint', {
                defaultValue: 'The drive storage quota for each tenant on this plan.',
              })} />
            </span>
            <GbInput
              bytes={cd.storageBytes}
              disabled={busy}
              ariaLabel={t('catalog.plans.col.storage', { defaultValue: 'Storage quota' })}
              draftKey="planCreate:new:typed:storage"
              onCommit={bytes => setCreateDraft(cur => ({ ...cur, storageBytes: bytes }))}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.concurrency')}
              <InfoHint text={t('catalog.plans.concurrencyHint', {
                defaultValue: 'How many AI jobs a tenant on this plan can run at once. Blank uses the built-in default; 0 stops every agent run.',
              })} />
            </span>
            <CapInput
              value={cd.concurrency}
              scale="int"
              disabled={busy}
              nullLabel={t('catalog.plans.concurrencyDefault', {
                defaultValue: 'Default ({{n}})',
                n: DEFAULT_AGENT_CONCURRENCY_CAP,
              })}
              ariaLabel={t('catalog.plans.col.concurrency')}
              inputWidthClass="w-24"
              draftKey="planCreate:new:typed:concurrency"
              onCommit={v => setCreateDraft(cur => ({ ...cur, concurrency: v }))}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.fairnessWeight', { defaultValue: 'Compute share' })}
              <InfoHint text={t('catalog.plans.fairnessWeightHint', {
                defaultValue: 'Relative share of a worker compute lane while tenants contend for it. Only the ratio between plans matters — a plan at 3 beside a plan at 1 gets three quarters of the lane. Every plan at the same number is plain round-robin, which is what a fresh deployment does.',
              })} />
            </span>
            {/* A plain integer input, not `CapInput`: the column is NOT NULL
                with a default of 1, so there is no null state to render and no
                "unset" label that would be true. */}
            <input
              value={cd.fairnessWeightText}
              disabled={busy}
              onChange={e => onCreateFairnessWeight(e.target.value)}
              inputMode="numeric"
              aria-label={t('catalog.plans.col.fairnessWeight', { defaultValue: 'Compute share' })}
              className="themed-input border rounded px-1.5 py-1 text-xs w-20 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          {/* Max ADDITIONAL seats — provider only, mirroring the plan-scope
              Row-1 field (blank = unlimited; 0 = none). Hidden under manual,
              where the included seats ARE the ceiling. */}
          {providerCreate && (
            <label className="flex flex-col gap-1">
              <span className="themed-text-secondary text-xs flex items-center gap-1">
                {t('catalog.plans.maxAdditionalSeats', { defaultValue: 'Max additional seats' })}
                <InfoHint text={t('catalog.plans.maxAdditionalSeatsHint', {
                  defaultValue: 'Seats past the included count that may be added under provider billing. Blank = unlimited; 0 = none.',
                })} />
              </span>
              <input
                value={cAdditionalValue}
                disabled={busy}
                onChange={e => onCreateAdditionalSeats(e.target.value)}
                inputMode="numeric"
                placeholder={t('catalog.unlimited')}
                aria-label={t('catalog.plans.maxAdditionalSeats', { defaultValue: 'Max additional seats' })}
                className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
              />
            </label>
          )}
        </div>

        {/* Uploads + request limits go through `createSavers` — the same
            `PlanSavers` shape `plan` scope uses, mutating `createDraft`
            instead of calling `catalogAdminApi`. No `bar`: design §5.4 — a
            single Confirm button (P2 Task 2, outside this component) drives
            the persist sequence, so each section keeps its own inline
            Save/Discard rather than registering into an aggregate bar. */}
        <PlanUploads plans={[draftAsPlan]} busy={busy} onSaved={async () => {}} savers={createSavers} draftKey="planCreate:new:uploads" t={t} />
        <PlanRateLimitsEditor plans={[draftAsPlan]} busy={busy} onSaved={async () => {}} savers={createSavers} draftKey="planCreate:new:rateLimits" t={t} />

        {/* The billing currency, LOCKED — never a selector. A new plan can
            only ever be priced in the deployment's own billing currency at
            creation time (design §5.3); per-currency/model/metered editing
            happens in the real plan editor Confirm opens. */}
        <div className="mt-4">
          <div className="themed-text-secondary text-xs mb-1">
            {t('catalog.plans.create.currencyLabel', { defaultValue: 'Priced in' })}
          </div>
          <div className="themed-text text-sm flex items-center gap-2">
            {defaultCurrency.toUpperCase()}
            <span className="text-[10px] px-1 py-0.5 rounded bg-blue-500/20 text-blue-300">
              {t('catalog.plans.prices.billing', { defaultValue: 'Billing' })}
            </span>
          </div>
          <p className="themed-text-muted text-[11px] mt-1">
            {t('catalog.plans.create.currencyHint', {
              defaultValue: 'This deployment’s billing currency — the only currency a new plan can be priced in. Set the price, AI model access and metered rates after creating the plan.',
            })}
          </p>
        </div>

        {/* Row 6 — the full billing-currency money row, blank, driven by the
            draft (0190 Phase 6). Mirrors the plan-scope `PriceSetPanel` Row 6:
            Free credits (`base_pool`), Extra (`extra_pool`), Grace
            (`grace_pool`), and — provider only — Price per extra seat
            (`base_price_minor`). Confirm gates on free credits (and, under
            provider, the per-seat price); extra/grace default 0 when blank. */}
        <div className="flex flex-wrap items-end gap-4 mt-3">
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.freeCredits', { defaultValue: 'Free credits' })}
              <InfoHint text={t('catalog.plans.addFreeCreditsHint', {
                defaultValue: 'The included AI allowance, in the plan currency. No platform default — you set it here.',
              })} />
            </span>
            <input
              value={cd.freeCreditsText}
              required
              disabled={busy || submitting}
              onChange={e => setCreateDraft(cur => ({ ...cur, freeCreditsText: e.target.value }))}
              inputMode="decimal"
              placeholder="0"
              aria-label={t('catalog.plans.col.freeCredits', { defaultValue: 'Free credits' })}
              aria-required="true"
              className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.planExtraPool', { defaultValue: 'Extra credits' })}
              <InfoHint text={t('catalog.plans.priceset.extraPoolHint', {
                defaultValue: 'The postpaid overage ceiling seeded onto tenants on this plan, in {{c}}. 0 = no overage.',
                c: defaultCurrency.toUpperCase(),
              })} />
            </span>
            <input
              value={cd.extraPoolText}
              disabled={busy || submitting}
              onChange={e => setCreateDraft(cur => ({ ...cur, extraPoolText: e.target.value }))}
              inputMode="decimal"
              placeholder="0"
              aria-label={t('catalog.plans.planExtraPool', { defaultValue: 'Extra credits' })}
              className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.planGracePool', { defaultValue: 'Grace credits' })}
              <InfoHint text={t('catalog.plans.priceset.gracePoolHint', {
                defaultValue: 'The grace allowance seeded onto tenants on this plan, in {{c}} — clamped to the platform grace ceiling. 0 (or blank) = inherit the platform grace ceiling (grace is NOT disabled — to turn grace off entirely, set the platform grace ceiling to 0).',
                c: defaultCurrency.toUpperCase(),
              })} />
            </span>
            <input
              value={cd.gracePoolText}
              disabled={busy || submitting}
              onChange={e => setCreateDraft(cur => ({ ...cur, gracePoolText: e.target.value }))}
              inputMode="decimal"
              placeholder="0"
              aria-label={t('catalog.plans.planGracePool', { defaultValue: 'Grace credits' })}
              className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          {providerCreate && (
            <label className="flex flex-col gap-1">
              <span className="themed-text-secondary text-xs flex items-center gap-1">
                {t('catalog.plans.pricePerExtraSeat', { defaultValue: 'Price per extra seat' })}
                <InfoHint text={t('catalog.plans.priceset.pricePerExtraSeatHint', {
                  defaultValue: 'What a member beyond the included seats is charged, per {{c}} billing cycle. This is the Stripe unit amount.',
                  c: defaultCurrency.toUpperCase(),
                })} />
              </span>
              <input
                value={cd.pricePerSeatText}
                required
                disabled={busy || submitting}
                onChange={e => setCreateDraft(cur => ({ ...cur, pricePerSeatText: e.target.value }))}
                inputMode="decimal"
                aria-label={t('catalog.plans.pricePerExtraSeat', { defaultValue: 'Price per extra seat' })}
                aria-required="true"
                className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
              />
            </label>
          )}
        </div>

        {/* Rows 7–8 (metered feature pricing) and Row 9 (AI models table),
            in DRAFT MODE (0190 Phase 6, Task 9) — seeded from the static
            catalog, editable before the plan exists, mirrored up onto the
            draft. `confirmDraft` persists them once the row is created. The
            `bar` (the create dialog's own `PlanEditBar`) hides their inline
            Save buttons; their registered `save` is a no-op. */}
        <FeaturePricingEditor
          scope="plan"
          scopeId="draft"
          currency={defaultCurrency}
          busy={busy || submitting}
          bar={bar}
          t={t}
          draftValue={cd.meteredDraft}
          onDraftChange={md => setCreateDraft(cur => ({ ...cur, meteredDraft: md }))}
        />
        <LlmPricing
          busy={busy || submitting}
          t={t}
          scope="plan"
          scopeId="draft"
          currency={defaultCurrency}
          bar={bar}
          title={t('catalog.plans.llm.planTitle', { defaultValue: 'AI models on this plan' })}
          desc={t('catalog.plans.llm.planDesc', {
            defaultValue:
              'Which AI models tenants on this plan may use, and the price they pay. Blank price inherits the built-in default rate.',
          })}
          draftValue={cd.modelDraft}
          onDraftChange={md => setCreateDraft(cur => ({ ...cur, modelDraft: md }))}
        />

        {/* ONE Confirm button drives the whole persist sequence (design
            §5.4 — no aggregate Save bar in `plan-create`). Disabled until the
            no-default fields are filled — name + free credits always, plus the
            per-seat price under provider billing (hidden/unrequired under
            manual). Presence only; malformed text is caught and toasted inside
            `confirmDraft` itself, same split `NewPlanForm` used. */}
        <div className="mt-4">
          <button
            type="button"
            onClick={confirmDraft}
            disabled={busy || submitting || !cd.name.trim() || !cd.freeCreditsText.trim() || (providerCreate && !cd.pricePerSeatText.trim())}
            className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
          >
            {submitting ? t('catalog.saving', { defaultValue: 'Saving…' }) : t('catalog.plans.create', { defaultValue: 'Create plan' })}
          </button>
        </div>
      </div>
    )
  }

  if (scope.kind === 'tenant') {
    const tp = scope.plan
    // The accessible names below identify the TENANT, never `tp.name` — that
    // is the shared PLAN's name, so two tenants on "Free" would present the
    // same control with the same accessible name while editing different
    // overrides. See `tenantName` in the scope type.
    // Bound formatter for the Row-6 money caps below — the exponent comes
    // from the currency, never a hardcoded `/100` (CLAUDE.md, whitelabel).
    const fmtMoney = (v: number) => formatMoney(v, { currency: defaultCurrency })
    const seatLabel = scope.seat.mode === 'included'
      ? t('settings.platform.customer.includedSeats', { defaultValue: 'Included seats' })
      : t('settings.platform.customer.maxSeats', { defaultValue: 'Max seats' })
    const concurrencyLabel = t('catalog.plans.col.concurrency')
    const freeCapLabel = t('settings.budget.col.freeCap')
    const extraCapLabel = t('settings.budget.col.extraCap')
    const graceCapLabel = t('settings.platform.customer.graceCap', { defaultValue: 'Grace ceiling' })
    return (
      <div key={scope.tenantId} className="px-5 py-4">
        {/* Row 1 — seats · storage · concurrency (modal order, Task 7). Seat
            and concurrency are `InheritedCapField`s (F2's inherits-unless-
            overwritten glance); storage stays a bare `GbInput` — its column
            is NOT NULL on `tenants`, so there is no inherit state to show
            (D4, accepted). `storageUseSlot` (the tenant-only utilisation bar
            — `@papan/billing` cannot read `row.storageUsedBytes`) renders
            beneath it. */}
        <div className="flex flex-wrap items-end gap-4 mb-4">
          {scope.seat.mode !== 'none' && (
            <InheritedCapField
              label={seatLabel}
              hint={scope.seat.mode === 'included'
                ? t('settings.platform.customer.includedSeatsHint', {
                    defaultValue:
                      'Seats this org is provisioned under manual billing — also its seat ceiling. '
                      + 'Raise it to grant more seats. Blank = inherit the plan; 0 = nobody. '
                      + 'The first owner is always seatable.',
                  })
                : t('settings.platform.customer.maxSeatsHint', {
                    defaultValue:
                      'Billable members this org may have. Blank = inherit the plan; 0 = nobody. '
                      + 'The first owner is always seatable.',
                  })}
              scale="int"
              saved={tenantSeatValue}
              inherited={scope.seat.inherited}
              nullMeans={t('settings.platform.customer.maxSeatsInherit', { defaultValue: 'Plan default' })}
              draftKey={`customer:${scope.tenantId}:typed:seat:${scope.seat.mode}`}
              onCommit={v => setTenantScalarDrafts(cur => ({ ...cur, seat: v, seatMode: scope.seat.mode }))}
              resetTarget="plan"
              zeroIsBlock
              format={String}
              disabled={busy || !canEdit}
              ariaLabel={`${scope.tenantName} — ${seatLabel}`}
              t={t}
            />
          )}
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.storage', { defaultValue: 'Storage quota' })}
              <InfoHint text={t('settings.platform.customer.storageOverrideHint', {
                defaultValue: 'This tenant’s storage quota, seeded from the plan’s. Enter an absolute size — there is no blank/inherit state for storage.',
              })} />
            </span>
            <GbInput
              bytes={tenantStorageValue}
              disabled={busy || !canEdit}
              ariaLabel={`${scope.tenantName} ${t('catalog.plans.col.storage', { defaultValue: 'Storage quota' })}`}
              draftKey={`customer:${scope.tenantId}:typed:storage`}
              onCommit={bytes => setTenantScalarDrafts(cur => ({ ...cur, storageBytes: bytes }))}
            />
            {storageUseSlot}
          </label>
          <InheritedCapField
            label={concurrencyLabel}
            hint={t('settings.platform.customer.concurrencyHintTenant', {
              defaultValue: 'Simultaneous agent runs. Blank falls back to the plan’s own cap (or the platform default).',
            })}
            scale="int"
            saved={tenantConcurrencyValue}
            inherited={tp.agentConcurrencyCap}
            nullMeans={tp.agentConcurrencyCap != null
              ? String(tp.agentConcurrencyCap)
              : t('catalog.plans.concurrencyDefault', {
                  defaultValue: 'Default ({{n}})', n: DEFAULT_AGENT_CONCURRENCY_CAP,
                })}
            draftKey={`customer:${scope.tenantId}:typed:concurrency`}
            onCommit={v => setTenantScalarDrafts(cur => ({ ...cur, concurrency: v }))}
            resetTarget="plan"
            zeroIsBlock={false}
            format={String}
            disabled={busy || !canEdit}
            ariaLabel={`${scope.tenantName} — ${concurrencyLabel}`}
            t={t}
          />
        </div>

        {/* Uploads → request limits, the plan-scope layout minus the CYCLE —
            each routed through the injected `savers` (`tenantPlanSavers`,
            `@papan/orgs`), gated on `canEdit` (`customers:write`).

            NO `PlanCycle` HERE, deliberately. 0185 moved the tenant's payment
            rhythm onto `manual_subscription_terms`: `buildAmountDue` reads the
            CURRENT TERM's cycle, and nothing reads `tenant_settings.cycle_*`
            for money any more. This control wrote exactly those columns, so it
            was a money control that changed no money — while displaying the
            PRESET's cycle (so the edit appeared not to stick after reload) and
            raising `PlanCycleImpact`'s ~x4.3 invoice warning for an effect that
            never happened. Three ways of lying about a charge.

            The plan for 0185 says the console's cycle control should write the
            TERM via `appendTerm` — but a term needs a plan id and an effective
            date this control has neither of, and the affordance that DOES
            collect them already exists: the Billing tab's term form
            (`SubscriptionHistory` → `addSubscriptionTerm`). So this is a
            deletion rather than a rewire — one source for the rhythm, which is
            what 0185 was for. The PRESET-scope `PlanCycle` below is untouched
            and still live: `resolveBillingCycle(null, preset)` reads it. */}
        <PlanUploads plans={[tp]} busy={busy || !canEdit} onSaved={onSaved} bar={canEdit ? bar : undefined} savers={savers} draftKey={`customer:${scope.tenantId}:uploads`} t={t} />
        <PlanRateLimitsEditor plans={[tp]} busy={busy || !canEdit} onSaved={onSaved} bar={canEdit ? bar : undefined} savers={savers} inheritedBaseline={scope.rateLimitBaseline} draftKey={`customer:${scope.tenantId}:rateLimits`} t={t} />

        {/* Row 6 — free / extra / grace caps (Task 7), each an
            `InheritedCapField` committing to the `tenantCaps` bar section
            (ONE `savers.saveCaps` call). Grace's hint states the platform
            ceiling it is clamped to (Task 4's `scope.platformGraceCeiling`) —
            0/blank inherits the PLAN's grace pool, not "grace disabled". */}
        <div className="mt-6 themed-text text-sm font-medium mb-1 flex items-center gap-1">
          {t('settings.platform.customer.limitsTitle', { defaultValue: 'Ceilings and limits' })}
          <InfoHint text={t('settings.budget.orgCeilingHint')} />
        </div>
        <div className="themed-bg-secondary rounded-lg p-3 flex flex-wrap items-end gap-4">
          <InheritedCapField
            label={freeCapLabel}
            hint={t('settings.platform.customer.freeCapHint', {
              defaultValue: 'The included allowance. Blank = unlimited; 0 = refuse all AI spend.',
            })}
            saved={tenantFreeCapValue}
            inherited={scope.freeCap.inherited}
            nullMeans={t('settings.budget.unlimited', { defaultValue: 'Unlimited' })}
            draftKey={`customer:${scope.tenantId}:typed:free`}
            onCommit={v => setTenantCapsDraft(cur => ({ ...cur, free: v }))}
            resetTarget="plan"
            format={fmtMoney}
            zeroIsBlock
            disabled={busy || !canEdit}
            ariaLabel={`${scope.tenantName} — ${freeCapLabel}`}
            t={t}
          />
          <InheritedCapField
            label={extraCapLabel}
            hint={t('settings.platform.customer.extraCapHint', {
              defaultValue: 'Spend beyond the included allowance. Blank = unlimited; 0 = stop at the allowance.',
            })}
            saved={tenantExtraCapValue}
            inherited={scope.extraCap.inherited}
            nullMeans={t('settings.budget.unlimited', { defaultValue: 'Unlimited' })}
            draftKey={`customer:${scope.tenantId}:typed:extra`}
            onCommit={v => setTenantCapsDraft(cur => ({ ...cur, extra: v }))}
            resetTarget="plan"
            format={fmtMoney}
            zeroIsBlock={false}
            disabled={busy || !canEdit}
            ariaLabel={`${scope.tenantName} — ${extraCapLabel}`}
            t={t}
          />
          <InheritedCapField
            label={graceCapLabel}
            hint={t('settings.platform.customer.graceCapHint', {
              defaultValue: 'Grace allowance for this tenant, seeded from the plan’s grace pool and clamped to the platform grace ceiling ({{ceiling}}). Blank = inherit the plan’s grace pool.',
              ceiling: fmtMoney(scope.platformGraceCeiling),
            })}
            saved={tenantGraceCapValue}
            inherited={scope.graceCap.inherited}
            nullMeans={t('settings.platform.customer.graceCapInherit', { defaultValue: 'Inherit platform ceiling' })}
            draftKey={`customer:${scope.tenantId}:typed:grace`}
            onCommit={v => setTenantCapsDraft(cur => ({ ...cur, grace: v }))}
            resetTarget="plan"
            format={fmtMoney}
            zeroIsBlock={false}
            disabled={busy || !canEdit}
            ariaLabel={`${scope.tenantName} — ${graceCapLabel}`}
            t={t}
          />
        </div>

        {/* Enforcement mode (Task 7) — a plain `<select>`, not an
            `InheritedCapField`: it is tenant-only, with no plan layer and no
            reset. Governs the org's OWN internal caps only — the platform
            ceilings above are always a hard stop, whichever mode is set. */}
        <div className="mt-4">
          <label className="flex flex-col gap-1 max-w-sm">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('settings.platform.customer.enforcementTitle', { defaultValue: 'When a member or team cap is reached' })}
              <InfoHint text={t('settings.platform.customer.enforcementHint', {
                defaultValue:
                  'This governs the org’s OWN internal caps only. The ceilings above are always a hard stop for the whole tenant, whichever mode is set.',
              })} />
            </span>
            <select
              value={tenantEnforcementDraft ?? scope.enforcementMode}
              disabled={busy || !canEdit}
              aria-label={`${scope.tenantName} — ${t('settings.platform.customer.enforcementTitle', { defaultValue: 'When a member or team cap is reached' })}`}
              onChange={e => setTenantEnforcementDraft(e.target.value as 'enforce' | 'warn_only')}
              className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
            >
              <option value="enforce">
                {t('settings.platform.customer.enforcementEnforce', { defaultValue: 'Refuse the request' })}
              </option>
              <option value="warn_only">
                {t('settings.platform.customer.enforcementWarn', { defaultValue: 'Allow it and warn' })}
              </option>
            </select>
          </label>
        </div>

        {/* Model access — the PLATFORM allow-list (`tenant_settings.allowed_models`),
            a different layer than the plan's own `features.models` (that one
            stays inside `LlmPricing`'s `allowedModels`, unused below — see
            the `currency` note ahead of `LlmPricing`). `onSaveTenantAllowedModels`
            is injected (not a `PlanSavers` axis — no plan/plan-create analog). */}
        <div className="mt-6 themed-text text-sm font-medium mb-2 flex items-center gap-1">
          {t('settings.platform.customer.modelsTitle')}
          <InfoHint text={`${t('settings.platform.customer.modelsPairDesc', {
            defaultValue:
              'Two things: what you allow, and what the tenant can actually use. They differ because their plan and their own admin narrow it further, and the org layer wins over the plan — so buying an upgrade would not unlock a model their admin has blocked.',
          })} ${t('settings.platform.customer.modelsDesc', {
            defaultValue: 'Models excluded here are hidden from this tenant entirely, not shown as restricted.',
          })}`} />
        </div>
        <div className="themed-bg-secondary rounded-lg p-3">
          <ModelAccessEditor
            value={scope.allowedModels}
            name={tp.name}
            busy={busy || !canEdit}
            draftKey={`customer:${scope.tenantId}:models`}
            onSave={async (m) => { await onSaveTenantAllowedModels?.(m) }}
            bar={canEdit ? bar : undefined}
          />
        </div>
        {resolvedModelAccessSlot && (
          <div className="mt-4">
            <div className="themed-text-secondary text-xs mb-2 flex items-center gap-1">
              {t('settings.platform.models.resolvedTitle', { defaultValue: 'What they can actually use' })}
              <InfoHint text={t('settings.platform.models.resolvedDesc', {
                defaultValue:
                  'What this tenant can actually use, after the platform allow-list, their plan, and their own admin’s restrictions.',
              })} />
            </div>
            {resolvedModelAccessSlot}
          </div>
        )}

        {/* Pricing for this tenant — `scope="tenant"`, `currency` LOCKED to
            the deployment's own billing currency (`defaultCurrency`), never a
            picked code (P4 Task 3's brief, absorbed here): tenant scope has
            no currency selector at all — see `PriceSetPanel`'s own docblock
            for why `plan`/`plan-create` scope need one and this does not (a
            tenant is always charged in the deployment's billing currency).
            No `allowedModels`/`onAllowedSave` passed to `LlmPricing` — that
            prop pair is the PLAN's own model restriction (`features.models`),
            a different layer than the `ModelAccessEditor` above. */}
        <FeaturePricingEditor
          scope="tenant"
          scopeId={scope.tenantId}
          currency={defaultCurrency}
          busy={busy || !canEditPricing}
          bar={canEditPricing ? bar : undefined}
          draftKey={`customer:${scope.tenantId}:metered`}
          t={t}
        />
        <LlmPricing
          busy={busy || !canEditPricing}
          t={t}
          scope="tenant"
          scopeId={scope.tenantId}
          currency={defaultCurrency}
          bar={canEditPricing ? bar : undefined}
          draftKey={`customer:${scope.tenantId}:llm`}
          title={t('settings.platform.customer.llmTitle', { defaultValue: 'AI model prices for this tenant' })}
          desc={t('settings.platform.customer.llmDesc', {
            defaultValue:
              'Overrides the plan and deployment-wide AI prices for this tenant only. Blank inherits. This sets what they are CHARGED — what the provider bills us is recorded separately, so the margin figure stays honest.',
          })}
        />

        {/* NO `<SaveBar>` here, unlike `plan` scope's return below — the host
            (`CustomerDetail.tsx`) already renders ONE `<DirtyBar>` for the
            whole Plan tab (PlanIdentity + the bespoke free/extra-cap +
            enforcement-mode block + everything above), and every section
            here registers into that SAME `bar`. A second bar would violate
            the "single Plan-tab DirtyBar" rule. */}
      </div>
    )
  }

  if (!isPlanScope || !p) {
    // Neither `plan` nor `tenant` (handled above) — currently unreachable,
    // kept as a safety net against a future scope variant landing here
    // without its own render branch.
    return null
  }

  // ── "Max additional seats" (Row 1, provider only) ───────────────────────
  // Stored as the absolute `max_seats` (`d.maxSeatsText`); shown and edited as
  // seats PAST the included count. Blank = unlimited. Editing the field commits
  // `max_seats = included + input`; changing included seats keeps the operator's
  // stated "additional" by recomputing the absolute ceiling (both directions).
  const includedSeatsNum = d ? parseOptionalInt(d.includedSeatsText) : undefined
  const includedSeatsSafe = typeof includedSeatsNum === 'number' ? includedSeatsNum : 0
  const maxSeatsNum = d ? parseOptionalInt(d.maxSeatsText) : undefined
  const additionalSeatsValue = typeof maxSeatsNum === 'number'
    ? String(Math.max(0, maxSeatsNum - includedSeatsSafe))
    : ''
  const onAdditionalSeatsChange = (text: string) => {
    const add = parseOptionalInt(text)
    if (add === null) { onDraftChange({ maxSeatsText: '' }); return }   // blank ⇒ unlimited
    if (typeof add === 'number') onDraftChange({ maxSeatsText: String(includedSeatsSafe + add) })
    // invalid (undefined) ⇒ drop the keystroke — this is a numeric field
  }
  const onFairnessWeightChange = (text: string) => {
    // See the create-scope twin: blank is allowed while typing, `saveScalars`
    // is what refuses an empty or out-of-range value.
    if (text === '' || /^\d{1,3}$/.test(text)) onDraftChange({ fairnessWeightText: text })
  }
  const onIncludedSeatsChange = (text: string) => {
    // Under manual billing there is no additional-seats field; keep it simple.
    if (planAuthority !== 'provider' || typeof maxSeatsNum !== 'number') {
      onDraftChange({ includedSeatsText: text }); return
    }
    const additional = Math.max(0, maxSeatsNum - includedSeatsSafe)
    const nextIncluded = parseOptionalInt(text)
    onDraftChange(typeof nextIncluded === 'number'
      ? { includedSeatsText: text, maxSeatsText: String(nextIncluded + additional) }
      : { includedSeatsText: text })
  }

  return (
    <div key={p.id} className="px-5 py-4">
      {/* The scalar fields, edited in the dialog. Name, free credits,
          included seats and storage moved here from the (now read-only)
          list row so one screen holds everything a plan gives. */}
      {d && (
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.name', { defaultValue: 'Name' })}
            </span>
            <input
              value={d.name}
              disabled={busy}
              onChange={e => onDraftChange({ name: e.target.value })}
              aria-label={`${d.name || p.id} ${t('catalog.plans.col.name')}`}
              className="themed-input border rounded px-1.5 py-1 text-xs w-40 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          {/* Audience removed from the plan editor (redesign): it only ever
              affected the customer plan picker / provider upsell and was inert
              under manual billing. `d.audience` is still seeded from the plan
              and re-sent unchanged by `saveScalars`, so the column is
              preserved. Free credits / per-seat price / extra + grace pools are
              PER CURRENCY — edited in the price-set panel below. */}
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.includedSeats', { defaultValue: 'Included seats' })}
              <InfoHint text={t('catalog.plans.includedSeatsHint', {
                defaultValue: 'Seats the plan provisions. Under manual billing this IS the seat ceiling; under provider billing, members beyond it are charged per extra seat.',
              })} />
            </span>
            <input
              value={d.includedSeatsText}
              disabled={busy}
              onChange={e => onIncludedSeatsChange(e.target.value)}
              inputMode="numeric"
              aria-label={`${d.name || p.id} ${t('catalog.plans.col.includedSeats', { defaultValue: 'Included seats' })}`}
              className="themed-input border rounded px-1.5 py-1 text-xs w-20 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.storage', { defaultValue: 'Storage quota' })}
              <InfoHint text={t('catalog.plans.storageHint', {
                defaultValue: 'The drive storage quota for each tenant on this plan.',
              })} />
            </span>
            <GbInput
              bytes={d.storageBytes}
              disabled={busy}
              ariaLabel={`${d.name || p.id} ${t('catalog.plans.col.storage')}`}
              draftKey={p ? `plan:${p.id}:typed:storage` : undefined}
              onCommit={bytes => onDraftChange({ storageBytes: bytes })}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.concurrency')}
              <InfoHint text={t('catalog.plans.concurrencyHint', {
                defaultValue: 'How many AI jobs a tenant on this plan can run at once. Blank uses the built-in default; 0 stops every agent run.',
              })} />
            </span>
            {/* The SAME control the tenant's Configuration tab uses.
                `nullLabel` is load-bearing: blank here falls back to
                DEFAULT_AGENT_CONCURRENCY_CAP, NOT to unlimited, and
                CapInput's default label would say the opposite of what
                the enforcer does. `zeroIsBlock` stays ON — a plan
                granting 0 concurrent jobs really does stop every agent
                run, so the red chip is the truth. */}
            <CapInput
              value={d.concurrency}
              scale="int"
              disabled={busy}
              nullLabel={t('catalog.plans.concurrencyDefault', {
                defaultValue: 'Default ({{n}})',
                n: DEFAULT_AGENT_CONCURRENCY_CAP,
              })}
              ariaLabel={`${d.name || p.id} ${t('catalog.plans.col.concurrency')}`}
              inputWidthClass="w-24"
              draftKey={p ? `plan:${p.id}:typed:concurrency` : undefined}
              onCommit={v => onDraftChange({ concurrency: v })}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-xs flex items-center gap-1">
              {t('catalog.plans.col.fairnessWeight', { defaultValue: 'Compute share' })}
              <InfoHint text={t('catalog.plans.fairnessWeightHint', {
                defaultValue: 'Relative share of a worker compute lane while tenants contend for it. Only the ratio between plans matters — a plan at 3 beside a plan at 1 gets three quarters of the lane. Every plan at the same number is plain round-robin, which is what a fresh deployment does.',
              })} />
            </span>
            {/* See the create-scope twin: NOT NULL with a default of 1, so a
                plain integer input rather than `CapInput` — there is no null
                state, and no "unset"/"unlimited" label that would be true. */}
            <input
              value={d.fairnessWeightText}
              disabled={busy}
              onChange={e => onFairnessWeightChange(e.target.value)}
              inputMode="numeric"
              aria-label={`${d.name || p.id} ${t('catalog.plans.col.fairnessWeight', { defaultValue: 'Compute share' })}`}
              className="themed-input border rounded px-1.5 py-1 text-xs w-20 outline-none focus:border-blue-500 disabled:opacity-50"
            />
          </label>
          {/* Max ADDITIONAL seats — the provider-mode ceiling, expressed as
              seats past the included count (persisted as absolute `max_seats`).
              Under manual billing the included seats ARE the ceiling, so this
              is hidden. Blank = unlimited; 0 = no seats beyond the included. */}
          {planAuthority === 'provider' && (
            <label className="flex flex-col gap-1">
              <span className="themed-text-secondary text-xs flex items-center gap-1">
                {t('catalog.plans.maxAdditionalSeats', { defaultValue: 'Max additional seats' })}
                <InfoHint text={t('catalog.plans.maxAdditionalSeatsHint', {
                  defaultValue: 'Seats past the included count that may be added under provider billing. Blank = unlimited; 0 = none.',
                })} />
              </span>
              <input
                value={additionalSeatsValue}
                disabled={busy}
                onChange={e => onAdditionalSeatsChange(e.target.value)}
                inputMode="numeric"
                // Blank = unlimited (the inherit chain's top). `0` here is a
                // real "no additional seats" ceiling, so it must be typed.
                placeholder={t('catalog.unlimited')}
                aria-label={`${d.name || p.id} ${t('catalog.plans.maxAdditionalSeats', { defaultValue: 'Max additional seats' })}`}
                className="themed-input border rounded px-1.5 py-1 text-xs w-24 outline-none focus:border-blue-500 disabled:opacity-50"
              />
            </label>
          )}
          {/* 'Order' (sort_order) removed from the plan editor: it is a
              pure display-sort integer with no pricing or entitlement
              effect. `sortText` stays seeded from the plan so saves keep
              the existing value; new plans append via nextSortOrder.

              The "Active" toggle was removed from the UI (the redesign):
              a plan is never manually retired from here for now. `isActive`
              stays on the draft (seeded from the plan) so saves preserve
              the existing value, and the column + PATCH support remain
              server-side for a future re-introduction. */}
        </div>
      )}

      {/* Each sub-editor scoped to THIS plan. They already map over a
          `plans` array, so a one-element array is the whole change —
          their validation, units and save paths are untouched.

          They keep their OWN Save bars rather than sharing one. Unifying
          them would mean lifting four independent draft states, each with
          its own validation (a currency-aware overage rate, the cycle
          CHECK constraint, a models JSONB spread), into this component —
          a large refactor of working code whose payoff is one button
          instead of four, on a panel where all four are now visible at
          once. The distance was the defect; the count is cosmetic. */}
      {/* Order (redesign): Allow uploads → Billing cycle → Request limits →
          Prices. The shared Save bar is order-independent (keyed Map), so the
          JSX order is purely presentational. */}
      <PlanUploads plans={[p]} busy={busy} onSaved={onSaved} bar={bar} savers={savers} draftKey={`plan:${p.id}:uploads`} t={t} />
      <PlanCycle plans={[p]} busy={busy} onSaved={onSaved} bar={bar} savers={savers} draftKey={`plan:${p.id}:cycle`} t={t} />
      <PlanRateLimitsEditor plans={[p]} busy={busy} onSaved={onSaved} bar={bar} savers={savers} draftKey={`plan:${p.id}:rateLimits`} t={t} />
      {/* Rows 5–9: currency selector (+ remove) → free/extra/grace/per-seat →
          metered feature pricing → AI models table, all under ONE currency
          selector. See `PriceSetPanel`'s own docblock above. */}
      <PriceSetPanel
        plan={p}
        currencies={currencies}
        defaultCurrency={defaultCurrency}
        planAuthority={planAuthority}
        busy={busy}
        onSaved={onSaved}
        bar={bar}
        barTotal={saveBarTotal}
        t={t}
      />

      {/* Tenant-specific controls — collapsed to the linked-tenant count +
          a Default chip; expanded to set-default and apply-to-tenants.
          Opens pre-expanded when the plan was opened from the Tenants
          count cell (`expandTenants`). */}
      <PlanTenantControls
        plan={p}
        planName={d?.name || p.name}
        isDefault={defaultPresetId === p.id}
        canSetDefault={!!d?.isActive}
        defaultExpanded={expandTenants}
        busy={busy}
        onSetDefault={onSetDefaultPreset}
        onPropagate={onPropagate}
        renderPlanTenants={renderPlanTenants}
        t={t}
      />

      {/* Rule 4 — stated where the grant-affecting fields are edited. */}
      <p className="themed-text-muted text-[11px] mt-3">{t('catalog.plans.entitlementNote')}</p>

      {/* ONE Save/Discard for the WHOLE dialog — every section registered
          into `bar` above. Appears only when something is dirty. */}
      <SaveBar count={saveBarTotal} busy={busy || saveBarBusy} onSave={onSaveAll} onDiscard={onDiscardAll} t={t} />
    </div>
  )
}
